import React, { createContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { linkTelegramAccount } from "./components/special/linkAccount.js";
import { sendWebAppOpen } from "./components/special/sendWebAppOpen";
import { Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import ListSearch from "./pages/events/ListSearch";
import AuthPage from "./pages/auth/AuthPage.jsx";
import api from "./api/axios";
import HostGuide from "./pages/events/HostGuide.jsx";
import HostRulesPage from "./pages/events/HostRulesPage.jsx";
import CommercialConditionsPage from "./pages/events/CommercialConditionsPage.jsx";
import PaidHostLegalPage from "./pages/events/PaidHostLegalPage.jsx";
import PaidTicketParticipantTermsPage from "./pages/events/PaidTicketParticipantTermsPage.jsx";
import { CastleSkinProvider } from "./context/CastleSkinContext.js";
import { ProfileCardProvider } from "./context/ProfileCardContext";
import { CheckinProvider } from './context/CheckinContext';
import { CheckinContext } from './context/CheckinContext';
import CheckinModal from './components/menu/StreakModal.jsx';
import GlobalErrorModal from './components/special/GlobalMessageModal.jsx';
import GlobalPhotoGuard from './components/special/GlobalPhotoGuard.jsx';
import BlockedAccountScreen from "./components/special/BlockedAccountScreen.jsx";
import LegalAcceptanceGate from "./components/special/LegalAcceptanceGate.jsx";
import HostClaimModal from './components/events/HostClaimModal.jsx';
import EventPromoUploadStatus from './components/events/EventPromoUploadStatus.jsx';
import { useIsDesktop } from "./hooks/useIsDesktop";
import AppShell from './components/layout/AppShell';
import PremiumRouteTransition from "./components/common/PremiumRouteTransition.jsx";
import { installPremiumRouteTransitionHistoryPatch } from "./utils/premiumRouteTransition.js";
import { ShopModeProvider } from "./context/ShopModeContext.jsx";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TasksProvider } from './context/TaskContext';
import { AppStateProvider } from './context/AppStateContext.jsx';
import { MailboxProvider } from './context/MailboxContext.jsx';
import NotificationToast from './components/menu/NotificationToast';
import ShopShell from './components/layout/ShopShell.jsx';
import UserAgreement from './pages/auth/UserAgreement.jsx';
import RussianUserAgreement from './pages/auth/RussianUserAgreement.jsx';
import PrivacyPolicy from './pages/auth/PrivacyPolicy.jsx';
import PersonalDataConsent from './pages/auth/PersonalDataConsent.jsx';
import CommunityRules from './pages/auth/CommunityRules.jsx';
import ModerationPolicy from './pages/auth/ModerationPolicy.jsx';
import CookiesPolicy from './pages/auth/CookiesPolicy.jsx';
import AccountDeletionPage from './pages/auth/AccountDeletionPage.jsx';
import ChildSafetyStandards from './pages/auth/ChildSafetyStandards.jsx';
import Landing from "./pages/landing/Landing";
import AboutPage from "./pages/landing/AboutPage";
import BratislavaEventsLanding from "./pages/landing/BratislavaEventsLanding";
import OrganizerLanding from "./pages/landing/OrganizerLanding";
import ParticipantLanding from "./pages/landing/ParticipantLanding";
import EmailConfirmPage from "./pages/auth/EmailConfirmationPage.jsx";
import RevolutVerification from './pages/special/info';
import IGGate from "./routes/IGGate";
import { useLocale } from "./context/LocaleContext.jsx"; 
import ChatOpen from "./components/menu/ChatOpen";
import RatingModal from "./components/rating/RatingModal.jsx";
import { ChatUnreadProvider} from "./context/ChatUnreadContext.jsx"; 
import NativeBadgeSync from "./components/common/NativeBadgeSync";
import { EventHostAttentionProvider } from "./context/EventHostAttentionContext.jsx";
import {
  ChatMessageNotificationToast,
  ChatRealtimeProvider,
} from "./context/ChatRealtimeContext.jsx";
import { StatisticsUnreadProvider } from "./context/StatisticsUnreadContext.jsx";
import { WaitlistUnreadProvider } from "./context/WaitlistUnreadContext.jsx";
import { ParticipantRequestUnreadProvider } from "./context/ParticipantRequestUnreadContext.jsx";
import { HostFeedbackUnreadProvider } from "./context/HostFeedbackUnreadContext.jsx";
import { ClubMemberUnreadProvider } from "./context/ClubMemberUnreadContext.jsx";
import { ClubRequestUnreadProvider } from "./context/ClubRequestUnreadContext.jsx";
import { ClubAttentionProvider } from "./context/ClubAttentionContext.jsx";
import { MutualUnreadProvider } from "./context/MutualUnreadContext.jsx";
import { AssistantUnreadProvider } from "./context/AssistantUnreadContext.jsx";
import { ParticipantRatingUnreadProvider } from "./context/ParticipantRatingUnreadContext.jsx";
import { EvaluationProvider, EvaluationContext } from "./context/EvaluationContext.jsx";
import { RequiredActionsProvider, RequiredActionsContext } from "./context/RequiredActionsContext.jsx";
import RequiredActionsModal from "./components/events/RequiredActionsModal.jsx";
import { CityLaunchProvider, CityLaunchContext } from "./context/CityLaunchContext.jsx";
import HostCapacityUpsellPromptModal from "./components/events/HostCapacityUpsellPromptModal.jsx";
import MutualConnectionsPromptModal from "./components/vault/MutualConnectionsPromptModal.jsx";
import MobileAppInviteController, {
  isMobileAppInviteWeb,
} from "./components/special/MobileAppInviteController.jsx";
import ModalCampaignController from "./components/special/ModalCampaignController.jsx";
import ReportedAgePromptModal from "./components/special/ReportedAgePromptModal.jsx";
import AssistantNotificationToast from "./components/assistant/AssistantNotificationToast.jsx";
import GuestAssistantLauncher from "./components/assistant/GuestAssistantLauncher.jsx";
import { isLocalDevelopmentHost } from "./utils/localDev.js";
import {
  getAnalyticsPageLocation,
  getAnalyticsPagePath,
  hasUnsafeAnalyticsQuery,
} from "./utils/analyticsUrl.js";
import CookieConsentBanner, {
  COOKIE_CONSENT_DECISION_KEY,
  COOKIE_CONSENT_VISITOR_KEY,
} from "./components/common/CookieConsentBanner.jsx";
import useNativeAppRuntime from "./hooks/useNativeAppRuntime.js";
import useNativeBuildTelemetry from "./hooks/useNativeBuildTelemetry.js";
import { removeAttendanceClaimFromLocation } from "./native/attendanceClaimRoute.js";
import {
  enableNativePushNotificationsAfterAuth,
  usePushNotifications,
} from "./hooks/usePushNotifications.js";
import { isNativeApp, isNativeIOS, openExternalUrl } from "./native/runtime.js";
import {
  getAppleExternalPurchaseAvailability,
  requestAppleExternalPurchaseTokens,
} from "./native/nativeExternalPurchase.js";
import { prepareEventCheckoutUrl } from "./utils/eventCheckout.js";
import {
  INCLUDE_INTERNAL_TOOLS,
  INCLUDE_MARKETING_ROUTES,
  IS_MOBILE_BUILD,
} from "./config/buildProfile.js";
import {
  isInternalToolingAvailable,
  isMobileAppRuntime,
} from "./config/mobileRuntime.js";
import { mergeAccountGateState } from "./utils/accountGateState.js";
import MapSearch from "./pages/events/MapSearchRoute.jsx";

const LeaveFlowSandboxPage = React.lazy(() =>
  import("./pages/events/LeaveFlowSandboxPage.jsx")
);
const ShopItem = React.lazy(() => import("./pages/shop/ShopItem"));
const Cart = React.lazy(() => import("./pages/shop/Cart"));
const Garment = React.lazy(() => import("./pages/shop/Garment"));
const Web3 = React.lazy(() => import("./pages/shop/Web3"));
const Catalog = React.lazy(() => import("./pages/shop/Catalog"));
const ShopDelivery = React.lazy(() => import("./pages/shop/Delivery"));
const Contact = React.lazy(() => import("./pages/shop/Contact"));

const EventCreateForm = React.lazy(() =>
  import("./pages/events/EventCreateForm")
);
const EventGalleryPage = React.lazy(() =>
  import("./pages/events/EventGalleryPage.jsx")
);
const ParticipantRatingPage = React.lazy(() =>
  import("./pages/events/ParticipantRatingPage.jsx")
);
const ChatsPage = React.lazy(() => import("./pages/menu/ChatsPage"));
const MailboxPage = React.lazy(() => import("./pages/menu/MailboxPage"));
const SinglesPage = React.lazy(() => import("./pages/singles/SinglesPage"));
const DinnersPage = React.lazy(() => import("./pages/dinners/DinnersPage"));
const TokenPage = React.lazy(() => import("./pages/menu/TokenPage"));
const HelpPage = React.lazy(() => import("./pages/menu/HelpPage"));
const SearchMain = React.lazy(() => import("./pages/search/SearchMain"));
const StorageOpen = React.lazy(() => import("./pages/search/StorageOpen"));
const VaultMain = React.lazy(() => import("./pages/vault/VaultMain"));
const VaultSettingsPage = React.lazy(() =>
  import("./pages/vault/VaultSettingsPage")
);
const SubscriptionConceptPage = React.lazy(() =>
  import("./pages/menu/SubscriptionConceptPage")
);
const BadgePage = React.lazy(() => import("./pages/menu/BadgePage"));
const Error404 = React.lazy(() => import("./pages/special/Error404.jsx"));
const EventPublicPage = React.lazy(() =>
  import("./pages/events/EventPublicPage.jsx")
);
const SilentDiscoPage = React.lazy(() =>
  import("./pages/events/SilentDiscoPage.jsx")
);
const ClubsPage = React.lazy(() => import("./pages/clubs/ClubsPage.jsx"));
const ClubPublicPage = React.lazy(() =>
  import("./pages/clubs/ClubPublicPage.jsx")
);
const ClubCreatePage = React.lazy(() =>
  import("./pages/clubs/ClubCreatePage.jsx")
);
const ClubManagePage = React.lazy(() =>
  import("./pages/clubs/ClubManagePage.jsx")
);
const ClubGalleryManagePage = React.lazy(() =>
  import("./pages/clubs/ClubGalleryManagePage.jsx")
);
const ClubOwnershipPage = React.lazy(() =>
  import("./pages/clubs/ClubOwnershipPage.jsx")
);
const ClubRequestsPage = React.lazy(() =>
  import("./pages/clubs/ClubRequestsPage.jsx")
);
const ClubMembersPage = React.lazy(() =>
  import("./pages/clubs/ClubMembersPage.jsx")
);
const PartyGamesVote = React.lazy(() =>
  import("./pages/events/PartyGameVote.jsx")
);
const PartySessionPage = React.lazy(() =>
  import("./pages/events/PartySessionPage.jsx")
);
const AssistantCorePage = React.lazy(() =>
  import("./pages/assistant/AssistantCorePage.jsx")
);
const InvoicePreviewPage = React.lazy(() =>
  import("./pages/special/InvoicePreviewPage.jsx")
);
const ModalSandboxPage = React.lazy(() =>
  import("./pages/special/ModalSandboxPage.jsx")
);
const PublicationCitySandboxPage = React.lazy(() =>
  import("./pages/events/PublicationCitySandboxPage.jsx")
);
const OnboardingSandboxPage = React.lazy(() =>
  import("./pages/special/OnboardingSandboxPage.jsx")
);
const RegistrationOnboardingPreviewPage = React.lazy(() =>
  import("./pages/special/RegistrationOnboardingPreviewPage.jsx")
);
const VaultGuidePlatformPage = React.lazy(() =>
  import("./pages/special/VaultGuidePlatformPage.jsx")
);
const SubscriptionPaymentPreviewPage = React.lazy(() =>
  import("./pages/menu/SubscriptionPaymentPreviewPage.jsx")
);
const KeySelectorPreviewPage = React.lazy(() =>
  import("./pages/special/KeySelectorPreviewPage.jsx")
);
const StaffMediaLibraryPage = React.lazy(() =>
  import("./pages/staff/StaffMediaLibraryPage.jsx")
);
const RouletteAdminPage = React.lazy(() =>
  import("./pages/admin/RouletteAdminPage.jsx")
);
const SocialRatingDebugPage = React.lazy(() =>
  import("./pages/admin/SocialRatingDebugPage.jsx")
);
const SinglesAnalyticsPage = React.lazy(() =>
  import("./pages/admin/SinglesAnalyticsPage.jsx")
);
const DinnersAdminPage = React.lazy(() =>
  import("./pages/admin/DinnersAdminPage.jsx")
);
const SportRatingDebugPage = React.lazy(() =>
  import("./pages/admin/SportRatingDebugPage.jsx")
);
const PartnerPlaceMapPickerPage = React.lazy(() =>
  import("./pages/admin/PartnerPlaceMapPickerPage.jsx")
);
// Keys context for search pages
export const KeysContext = createContext({
  keys: null,
  refreshKeys: () => {},
  mergeKeys: async () => {},
});

const queryClient = new QueryClient();
const DEFAULT_GA_MEASUREMENT_ID = "G-P32JF4Y1SD";
const GA_MEASUREMENT_ID =
  IS_MOBILE_BUILD
    ? ""
    : process.env.REACT_APP_GA_MEASUREMENT_ID || DEFAULT_GA_MEASUREMENT_ID;
const DEFAULT_META_PIXEL_ID = "1672118793931658";
// Meta Pixel remains a web-only analytics integration. Loading it in the
// native WebView would require an ATT flow on iOS, which is intentionally not
// part of the mobile product.
const META_PIXEL_ID = IS_MOBILE_BUILD
  ? ""
  : process.env.REACT_APP_META_PIXEL_ID || DEFAULT_META_PIXEL_ID;

const isOrganizerLandingPath = (pathname) => {
  const normalized = String(pathname || "").replace(/\/+$/, "") || "/";
  return /^\/(?:for-organizers|organizers)(?:\/(?:en|sk|cs))?$/.test(normalized);
};
const isParticipantLandingPath = (pathname) => {
  const normalized = String(pathname || "").replace(/\/+$/, "") || "/";
  return /^\/(?:for-participants|participants)(?:\/(?:en|sk|cs))?$/.test(normalized);
};
const getCommunityLandingLanguage = (pathname) => {
  const normalized = String(pathname || "").replace(/\/+$/, "") || "/";
  if (normalized.endsWith("/sk")) return "sk";
  if (normalized.endsWith("/cs")) return "cs";
  if (normalized.endsWith("/en")) return "en";
  return null;
};

function ensureMetaPixelLoaded(pixelId) {
  if (typeof window === "undefined" || !pixelId) return false;

  window.__vgMetaPixelConsentGranted = true;

  if (window.__vgMetaPixelLoaded === true && typeof window.fbq === "function") {
    return true;
  }

  if (typeof window.fbq !== "function") {
    const fbq = function fbqProxy() {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, arguments);
      } else {
        fbq.queue.push(arguments);
      }
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];
    window.fbq = fbq;
    window._fbq = fbq;
  }

  if (!document.querySelector('script[src="https://connect.facebook.net/en_US/fbevents.js"]')) {
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);
  }

  if (window.__vgMetaPixelInitializedFor !== pixelId) {
    window.fbq("init", pixelId);
    window.__vgMetaPixelInitializedFor = pixelId;
  }

  window.__vgMetaPixelLoaded = true;
  return true;
}

const EventBuyRedirect = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!id || typeof window === "undefined") return;

    let active = true;

    const base = api?.defaults?.baseURL || "/api";
    let origin = window.location.origin;
    let basePath = "";

    try {
      const parsedBase = new URL(base, origin);
      origin = parsedBase.origin;
      basePath = parsedBase.pathname.replace(/\/$/, "");
    } catch {
      basePath = String(base || "").replace(/\/$/, "");
    }

    const search = location?.search || "";
    const checkoutUrl = `${origin}${basePath}/events/${id}/buy/${search}`;
    void prepareEventCheckoutUrl({ directUrl: checkoutUrl, eventId: id })
      .then(async (providerCheckoutUrl) => {
        if (!active) return;
        if (isNativeApp()) {
          await openExternalUrl(providerCheckoutUrl);
          if (active) navigate(`/events/${id}`, { replace: true });
          return;
        }
        window.location.replace(providerCheckoutUrl);
      })
      .catch(() => {
        if (active) navigate(`/events/${id}`, { replace: true });
      });

    return () => {
      active = false;
    };
  }, [id, location?.search, navigate]);

  return null;
};
const IOS_SYSTEM_CHROME_COLOR = "#0c0a09";
const BOOT_SPLASH_EXIT_MS = 440;
const ADMIN_REF_SUFFIX_RE = /\/(REF\d+)\/?$/i;
const ADMIN_REF_VISITOR_ID_STORAGE_KEY = "vg_ref_track_visitor_id";

function createLocalId(prefix) {
  try {
    if (typeof window !== "undefined" && window.crypto?.randomUUID) {
      return `${prefix}-${window.crypto.randomUUID()}`;
    }
  } catch {
    // ignore
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function getOrCreateStoredValue(key, prefix) {
  try {
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const created = createLocalId(prefix);
    localStorage.setItem(key, created);
    return created;
  } catch {
    return createLocalId(prefix);
  }
}

function extractAdminRefCodeFromPath(pathname) {
  const value = String(pathname || "");
  const match = value.match(ADMIN_REF_SUFFIX_RE);
  return match ? String(match[1] || "").toUpperCase() : "";
}

function stripAdminRefCodeFromPath(pathname) {
  const value = String(pathname || "");
  const stripped = value.replace(ADMIN_REF_SUFFIX_RE, "").replace(/\/+$/, "");
  return stripped || "/";
}

function getStoredAssistantOnboardingSource() {
  try {
    const stored = String(localStorage.getItem("assistant_onboarding_source") || "")
      .trim()
      .toLowerCase();
    return stored.startsWith("landing") ? stored : "";
  } catch {
    return "";
  }
}

function readStoredCookieConsentDecision() {
  try {
    const value = String(localStorage.getItem(COOKIE_CONSENT_DECISION_KEY) || "").trim();
    if (value === "accepted" || value === "rejected") return value;
  } catch {
    // ignore
  }
  return "";
}

function detectAssistantBootstrapLanguage(language) {
  const normalized = String(language || "")
    .trim()
    .toLowerCase()
    .split("-")[0];
  if (normalized === "ru" || normalized === "sk" || normalized === "cs") return normalized;
  return "en";
}

function broadcastAssistantSessionUpdated(sessionId) {
  try {
    window.dispatchEvent(
      new CustomEvent("vg-assistant-session-updated", {
        detail: { sessionId: String(sessionId || "").trim() },
      })
    );
  } catch {
    // ignore
  }
}

function broadcastAssistantUnreadRefresh() {
  try {
    window.dispatchEvent(new CustomEvent("vg-assistant-unread-refresh"));
  } catch {
    // ignore
  }
}

// Route guard: referral normalization only. Instagram gate is temporarily disabled.
function RouterGuard({ children }) {
  const loc = useLocation();
  const nav = useNavigate();
  const hasRefSuffix = Boolean(extractAdminRefCodeFromPath(loc.pathname));

  useEffect(() => {
    const refCode = extractAdminRefCodeFromPath(loc.pathname);
    if (!refCode) return;

    const cleanPath = stripAdminRefCodeFromPath(loc.pathname);
    const cleanNext = `${cleanPath}${loc.search || ""}${loc.hash || ""}` || "/";
    const sourcePath = `${loc.pathname || ""}${loc.search || ""}${loc.hash || ""}`;
    const visitorId = getOrCreateStoredValue(
      ADMIN_REF_VISITOR_ID_STORAGE_KEY,
      "refv"
    );

    api.post(
      "/miniapp/admin-referrals/track-click/",
      {
        ref_code: refCode,
        source_path: sourcePath,
        normalized_path: cleanNext,
        visitor_id: visitorId,
      },
      {
        meta: {
          authOptional: true,
          suppressUnauthorizedRedirect: true,
          suppressGlobalError: true,
        },
      }
    ).catch(() => {});

    nav(cleanNext, { replace: true });
  }, [loc.pathname, loc.search, loc.hash, nav]);

  if (hasRefSuffix) return null;

  return children;
}


// Wrapper: renders children only if user is set, otherwise redirects to the login auth screen.
const getLocationPath = (location) =>
  `${location?.pathname || ""}${location?.search || ""}${location?.hash || ""}` || "/";

const buildLoginAuthPath = (nextPath = "") => {
  const params = new URLSearchParams();
  params.set("tab", "login");
  if (nextPath) {
    params.set("next", nextPath);
  }
  return `/auth?${params.toString()}`;
};

const PrivateRoute = ({ user, loading, children }) => {
  const location = useLocation();
  if (loading) return null;
  if (!user) {
    return <Navigate to={buildLoginAuthPath(getLocationPath(location))} replace />;
  }
  return children;
};

const AuthRedirect = () => {
  const location = useLocation();
  return <Navigate to={buildLoginAuthPath(getLocationPath(location))} replace />;
};

const RootRoute = ({ user, loading }) => {
  if (!isMobileAppRuntime()) return <Landing />;
  if (loading) return null;
  return (
    <Navigate
      to={user ? "/events/search" : buildLoginAuthPath()}
      replace
    />
  );
};

function App() {
  // state for the authenticated user; the setter is passed down to AuthPage
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assistantRolloutLoading, setAssistantRolloutLoading] = useState(true);
  const [assistantProductionOnboardingEnabled, setAssistantProductionOnboardingEnabled] = useState(false);
  const [cookieConsentDecision, setCookieConsentDecision] = useState(() => readStoredCookieConsentDecision());
  const [mobileAppInviteHasPriority, setMobileAppInviteHasPriority] = useState(() => isMobileAppInviteWeb());
  const [modalCampaignHasPriority, setModalCampaignHasPriority] = useState(true);
  const [agePromptHasPriority, setAgePromptHasPriority] = useState(false);
  const backgroundAssistantBootstrapStartedRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const guideIsDesktop = useIsDesktop();
  const guideOverlayOpen = guideIsDesktop &&
    (location.pathname === "/guide" || location.pathname === "/local-debug/vault-guide");
  const savedGuideBackground = location.state?.guideBackgroundLocation;
  const guideBackgroundLocation = guideOverlayOpen
    ? (savedGuideBackground?.pathname?.startsWith("/vault/")
        ? savedGuideBackground
        : { pathname: "/vault/main", search: "", hash: "" })
    : location;
  const { language, setCountry, setLanguage } = useLocale(); // 👈 берём из контекста
  const internalToolsAvailable =
    INCLUDE_INTERNAL_TOOLS && isInternalToolingAvailable();
  const localDebugRoutesEnabled =
    internalToolsAvailable && isLocalDevelopmentHost();
  const isPublicationCitySandbox = localDebugRoutesEnabled
    && location.pathname === "/local-debug/publication-city-sandbox";
  useNativeAppRuntime({ navigate, location });
  useNativeBuildTelemetry(!loading ? user?.id : null);
  usePushNotifications(Boolean(user));

  useEffect(() => {
    if (loading || !user?.id || !isNativeIOS()) return undefined;
    let cancelled = false;
    const registerLaunchTokens = async () => {
      try {
        const pricing = await api.get("/accounts/subscription-pricing/", {
          meta: { suppressGlobalError: true },
        });
        if (cancelled || !pricing.data?.apple_web_checkout_enabled) return;
        const availability = await getAppleExternalPurchaseAvailability();
        if (cancelled || !availability?.eligible) return;
        const handoff = await requestAppleExternalPurchaseTokens();
        if (cancelled) return;
        await api.post("/accounts/apple-external-purchase/tokens/", {
          apple_external_purchase_tokens: handoff.tokens,
          apple_storefront_country: handoff.storefrontCountry,
        }, { meta: { suppressGlobalError: true } });
      } catch (error) {
        // The paywall retries registration before any external payment.
        console.warn("Apple external purchase token registration failed", error);
      }
    };
    registerLaunchTokens();
    return () => { cancelled = true; };
  }, [loading, user?.id]);

  useEffect(() => {
    if (loading || typeof document === "undefined") return undefined;

    const splash = document.getElementById("vg-boot-splash");
    if (!splash) return undefined;

    const frameId = window.requestAnimationFrame(() => {
      splash.classList.add("vg-boot-splash--leaving");
    });
    const removeTimerId = window.setTimeout(() => {
      window.__VG_NATIVE_BOOT__?.mark?.("app_ready");
      if (window.__VG_NATIVE_BOOT_WATCHDOG__ != null) {
        window.clearTimeout(window.__VG_NATIVE_BOOT_WATCHDOG__);
      }
      splash.remove();
      document.documentElement.classList.remove("vg-native-boot");
      document.documentElement.classList.remove("vg-native-boot--stalled");
    }, BOOT_SPLASH_EXIT_MS);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(removeTimerId);
    };
  }, [loading]);

  useEffect(() => {
    if (loading || !user?.id || !isNativeApp()) return;

    enableNativePushNotificationsAfterAuth().catch((error) => {
      console.warn("Native push permission prompt skipped", error);
    });
  }, [loading, user?.id]);

  useEffect(() => installPremiumRouteTransitionHistoryPatch(), []);

  // Host QR claim modal state (opened only via QR deep link)
  const [hostClaimToken, setHostClaimToken] = useState(null);
  const [hostClaimOpen, setHostClaimOpen] = useState(false);
  const dismissedHostClaimTokenRef = useRef(null);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const themeMeta = document.querySelector('meta[name="theme-color"]');
    const previousThemeColor = themeMeta?.getAttribute("content") || null;
    const previousHtmlBg = document.documentElement.style.backgroundColor;
    const previousBodyBg = document.body?.style?.backgroundColor || "";

    if (themeMeta) {
      themeMeta.setAttribute("content", IOS_SYSTEM_CHROME_COLOR);
    }
    document.documentElement.style.backgroundColor = IOS_SYSTEM_CHROME_COLOR;
    if (document.body) {
      document.body.style.backgroundColor = IOS_SYSTEM_CHROME_COLOR;
    }

    return () => {
      if (themeMeta) {
        if (previousThemeColor == null) {
          themeMeta.removeAttribute("content");
        } else {
          themeMeta.setAttribute("content", previousThemeColor);
        }
      }
      document.documentElement.style.backgroundColor = previousHtmlBg;
      if (document.body) {
        document.body.style.backgroundColor = previousBodyBg;
      }
    };
  }, []);

  useEffect(() => {
    const syncDecision = () => {
      setCookieConsentDecision(readStoredCookieConsentDecision());
    };

    syncDecision();
    window.addEventListener("storage", syncDecision);
    window.addEventListener("vg-cookie-consent-updated", syncDecision);
    return () => {
      window.removeEventListener("storage", syncDecision);
      window.removeEventListener("vg-cookie-consent-updated", syncDecision);
    };
  }, []);

  // Load Google Analytics only after explicit analytics consent.
  useEffect(() => {
    if (cookieConsentDecision !== "accepted") return;
    if (!GA_MEASUREMENT_ID) return;

    const existing = document.querySelector(
      `script[src*="gtag/js?id=${GA_MEASUREMENT_ID}"]`
    );
    if (!existing) {
      const gaScript = document.createElement("script");
      gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      gaScript.async = true;
      document.head.appendChild(gaScript);
    }

    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    // Disable automatic page_view to avoid duplicates in SPA.
    window.gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });
  }, [cookieConsentDecision]);

  // Runtime kill switch for GA when consent is not accepted.
  useEffect(() => {
    if (!GA_MEASUREMENT_ID) return;
    window[`ga-disable-${GA_MEASUREMENT_ID}`] = cookieConsentDecision !== "accepted";
  }, [cookieConsentDecision]);

  // Load Meta Pixel only after explicit analytics consent.
  useEffect(() => {
    if (cookieConsentDecision !== "accepted") {
      if (typeof window !== "undefined") {
        window.__vgMetaPixelConsentGranted = false;
      }
      return;
    }
    if (!META_PIXEL_ID) return;
    ensureMetaPixelLoaded(META_PIXEL_ID);
  }, [cookieConsentDecision]);

  // Bind authenticated user id to GA so BigQuery exports can be joined with backend user_id.
  useEffect(() => {
    if (cookieConsentDecision !== "accepted") return;
    if (!GA_MEASUREMENT_ID) return;
    if (typeof window.gtag !== "function") return;
    // Keep send_page_view disabled here too, otherwise config updates can create duplicate views.
    if (user?.id) {
      window.gtag("config", GA_MEASUREMENT_ID, {
        user_id: String(user.id),
        send_page_view: false,
      });
    } else {
      // Explicitly clear on logout/guest mode.
      window.gtag("config", GA_MEASUREMENT_ID, {
        user_id: null,
        send_page_view: false,
      });
    }
  }, [cookieConsentDecision, user?.id]);

  // Manual SPA page_view tracking on every route change.
  useEffect(() => {
    if (cookieConsentDecision !== "accepted") return;
    if (!GA_MEASUREMENT_ID) return;
    if (typeof window.gtag !== "function") return;
    window.gtag("event", "page_view", {
      page_title: document.title,
      page_location: getAnalyticsPageLocation(window.location),
      page_path: getAnalyticsPagePath({
        pathname: location.pathname,
        search: location.search,
      }),
    });
  }, [cookieConsentDecision, location.pathname, location.search]);

  useEffect(() => {
    if (cookieConsentDecision !== "accepted") return;
    if (!META_PIXEL_ID) return;
    // Meta Pixel derives its URL from the live document and does not expose a
    // reliable per-event URL override. Suppress PageView when any unapproved
    // query parameter is present so tokens and personal data cannot leak.
    if (hasUnsafeAnalyticsQuery(location.search)) return;
    if (!ensureMetaPixelLoaded(META_PIXEL_ID)) return;
    if (typeof window.fbq !== "function") return;
    window.fbq("track", "PageView");
  }, [cookieConsentDecision, location.pathname, location.search]);

  // Auto-open HostClaimModal when URL contains ?host_claim=TOKEN
  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const token = search.get('host_claim');
    if (token) {
      if (dismissedHostClaimTokenRef.current === token) return;
      setHostClaimToken(token);
      setHostClaimOpen(true);
    } else if (location.pathname !== "/auth") {
      dismissedHostClaimTokenRef.current = null;
      setHostClaimOpen(false);
      setHostClaimToken(null);
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const token = search.get("host_claim");
    if (!token || location.pathname === "/auth") return;

    const isEventSearchDetailsLink =
      location.pathname === "/events/search" && search.has("event");
    const singleEventPathMatch = location.pathname.match(/^\/events\/([^/]+)\/?$/);
    const singleEventPathSegment = singleEventPathMatch?.[1] || "";
    const reservedEventPathSegments = new Set([
      "create",
      "edit",
      "map_search",
      "my-tickets",
      "participant-rating",
      "search",
    ]);
    const isSingleEventPublicPath =
      singleEventPathSegment &&
      !reservedEventPathSegments.has(singleEventPathSegment);

    if (!isEventSearchDetailsLink && !isSingleEventPublicPath) return;

    const nextSearch = new URLSearchParams();
    nextSearch.set("host_claim", token);
    navigate(`/events/search?${nextSearch.toString()}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const token = search.get("host_claim");
    if (loading || !token || user || location.pathname === "/auth") return;
    navigate(buildLoginAuthPath(getLocationPath(location)), {
      replace: true,
      state: { from: location },
    });
  }, [loading, location, navigate, user]);

  // Вызываем только когда понятен статус и пользователь авторизован
  useEffect(() => {
    if (!loading && user && !user?.blocked) {
      sendWebAppOpen();
    }
  }, [loading, user]);

  useEffect(() => {
    if (loading || !user?.id) return;

    let decision = "";
    let visitorId = "";
    try {
      decision = String(localStorage.getItem(COOKIE_CONSENT_DECISION_KEY) || "").trim();
      visitorId = String(localStorage.getItem(COOKIE_CONSENT_VISITOR_KEY) || "").trim();
    } catch {
      return;
    }

    if (decision !== "accepted" && decision !== "rejected") return;
    const syncKey = `vg_cookie_consent_synced_user_${user.id}_${decision}`;
    try {
      if (localStorage.getItem(syncKey) === "1") return;
    } catch {
      // continue without local dedupe
    }

    let cancelled = false;
    api
      .post(
        "/accounts/cookie-consent/",
        {
          decision,
          visitor_id: visitorId,
          source_path: `${location.pathname || "/"}${location.search || ""}`,
          banner_version: "v1",
        },
        {
          meta: {
            authOptional: true,
            suppressUnauthorizedRedirect: true,
            suppressGlobalError: true,
          },
        }
      )
      .then((response) => {
        if (cancelled) return;
        try {
          localStorage.setItem(syncKey, "1");
        } catch {
          // ignore
        }
        const cookieConsent = response?.data?.cookie_consent;
        if (cookieConsent) {
          setUser((prev) => (
            prev && typeof prev === "object"
              ? { ...prev, cookie_consent: cookieConsent }
              : prev
          ));
        }
      })
      .catch(() => {
        // Consent was already saved for the visitor; user linking can retry later.
      });

    return () => {
      cancelled = true;
    };
  }, [loading, user?.id, location.pathname, location.search]);

  // --- one-time CSRF bootstrap ---
  useEffect(() => {
    if (isNativeApp()) return;
    api.get("/csrf/", {
      meta: { suppressUnauthorizedRedirect: true, authOptional: true },
    }).catch(() => {
      console.warn("CSRF init failed");
    });
  }, []);

  useEffect(() => {
    if (!isNativeApp()) return undefined;

    const refreshNativeProfile = async (event) => {
      try {
        const response = await api.get("/accounts/profile/", {
          meta: { suppressUnauthorizedRedirect: true, authOptional: true },
        });
        const nextUser = response?.data;
        if (nextUser && typeof nextUser === "object" && nextUser.id) {
          setUser(nextUser);
          if (event?.type === "vg:native-oauth-complete") {
            const nextPath = event?.detail?.next;
            navigate(nextPath && nextPath.startsWith("/") ? nextPath : "/events/search", {
              replace: true,
            });
          }
        }
      } catch {
        // Offline resume and cancelled OAuth are handled by the existing UI.
      }
    };

    const handleOAuthError = (event) => {
      navigate("/auth", { replace: true });
      window.dispatchEvent(new CustomEvent("global-error", {
        detail: {
          status: 400,
          message: event?.detail?.error || "Sign-in failed",
        },
      }));
    };

    window.addEventListener("vg:native-resume", refreshNativeProfile);
    window.addEventListener("vg:native-oauth-complete", refreshNativeProfile);
    window.addEventListener("vg:native-oauth-error", handleOAuthError);
    return () => {
      window.removeEventListener("vg:native-resume", refreshNativeProfile);
      window.removeEventListener("vg:native-oauth-complete", refreshNativeProfile);
      window.removeEventListener("vg:native-oauth-error", handleOAuthError);
    };
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;

    api
      .get("/assistant/public-config/", {
        meta: {
          suppressUnauthorizedRedirect: true,
          authOptional: true,
          suppressGlobalError: true,
        },
      })
      .then((res) => {
        if (cancelled) return;
        setAssistantProductionOnboardingEnabled(
          Boolean(res?.data?.production_onboarding_enabled)
        );
      })
      .catch(() => {
        if (cancelled) return;
        setAssistantProductionOnboardingEnabled(false);
      })
      .finally(() => {
        if (!cancelled) setAssistantRolloutLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Refresh user profile after avatar upload (used by GlobalPhotoGuard and other flows)
  useEffect(() => {
    const handler = (e) => {
      console.log("[App] vg-avatar-updated", e?.detail);
      const newUrl = e?.detail?.avatar_custom_url;

      // Optimistic patch: hide GlobalPhotoGuard immediately
      if (newUrl) {
        setUser((prev) => {
          if (!prev || typeof prev !== "object") return prev;
          return {
            ...prev,
            avatar_custom_url: newUrl,
            photo_required_now: false,
          };
        });
        console.log("[App] user patched after avatar upload");
      }
      // No longer performing hard refresh from backend here.
    };

    window.addEventListener("vg-avatar-updated", handler);
    return () => window.removeEventListener("vg-avatar-updated", handler);
  }, []);

  const isPublicPath = React.useCallback((path) => {
    // The referral suffix is removed by RouterGuard after the first render.
    // Until then, guest access must be evaluated against the destination path
    // so the auth redirect cannot race ahead of referral normalization.
    if (extractAdminRefCodeFromPath(path)) {
      path = stripAdminRefCodeFromPath(path);
    }
    if (
      path === "/local-debug/modal-sandbox" &&
      isLocalDevelopmentHost()
    ) return true;
    if (path === "/local-debug/paid-event-creation" && isLocalDevelopmentHost()) return true;
    if (path === "/local-debug/publication-city-sandbox" && isLocalDevelopmentHost()) return true;
    if (
      (path === "/local-debug/registration-onboarding" ||
        path === "/local-debug/registration-onboarding/cards" ||
        path === "/local-debug/vault-guide" ||
        path === "/local-debug/subscription-payment" ||
        path === "/local-debug/key-selector") &&
      isLocalDevelopmentHost()
    ) return true;
    if (
      path === "/" ||
      path === "/auth" ||
      path === "/info" ||
      path === "/about" ||
      path === "/podujatia-bratislava" ||
      path === "/podujatia-bratislava/"
    ) return true;
    if (isOrganizerLandingPath(path)) return true;
    if (isParticipantLandingPath(path)) return true;
    if (path.startsWith("/about/")) return true;
    if (path.startsWith("/accounts/confirm-email")) return true;
    if (path.startsWith("/legal/")) return true;
    if (/^\/commercial-conditions(?:\/(?:en|ru|sk))?\/?$/i.test(path)) return true;
    if (path === "/account-deletion" || path === "/account-deletion/") return true;
    if (path.startsWith("/host-guide")) return true;
    if (path.startsWith("/hostguide")) return true;
    if (path === "/clubs" || path === "/clubs/") return true;
    if (path === "/events" || path === "/events/") return true;
    if (path.startsWith("/events/search")) return true;
    if (path.startsWith("/assistant/core")) return true;
    if (/^\/events\/\d+(?:-[^/?#]+)?\/?$/.test(path)) return true;
    if (/^\/clubs\/\d+(?:-[^/?#]+)?\/?$/.test(path)) return true;
    if (path.startsWith("/ig-gate")) return true;
    return false;
  }, []);

  // Guests may only visit public routes; enforce redirect for others
  useEffect(() => {
    if (loading) return;
    if (!user && !isPublicPath(location.pathname)) {
      navigate(buildLoginAuthPath(getLocationPath(location)), { replace: true, state: { from: location } });
    }
  }, [user, loading, location, location.pathname, isPublicPath, navigate]);

  // <add unauthorized listener>
  useEffect(() => {
    const handler = () => {
      setUser(null);
      if (!isPublicPath(location.pathname)) {
        navigate(buildLoginAuthPath(getLocationPath(location)), { replace: true });
      }
    };
    window.addEventListener("unauthorized", handler);
    return () => window.removeEventListener("unauthorized", handler);
  }, [navigate, location, isPublicPath]);
  // </add unauthorized listener>

  useEffect(() => {
    const handler = (event) => {
      setUser((prev) => ({
        ...(prev && typeof prev === "object" ? prev : {}),
        blocked: true,
        block_reason: event?.detail?.block_reason || "",
        blocked_reason: event?.detail?.error || "",
      }));
    };
    window.addEventListener("account-blocked", handler);
    return () => window.removeEventListener("account-blocked", handler);
  }, []);

  useEffect(() => {
    const handler = () => {
      setUser((prev) => (
        prev && typeof prev === "object"
          ? { ...prev, legal_reaccept_required: true }
          : prev
      ));
    };
    window.addEventListener("legal-acceptance-required", handler);
    return () => window.removeEventListener("legal-acceptance-required", handler);
  }, []);

  useEffect(() => {
    if (loading || !user?.id || user?.blocked || user?.legal_reaccept_required) return undefined;

    let cancelled = false;
    const checkAccountGateState = async () => {
      try {
        const response = await api.get("/accounts/moderation/decision/", {
          meta: {
            suppressUnauthorizedRedirect: true,
            authOptional: true,
            suppressGlobalError: true,
          },
        });
        if (cancelled) return;
        if (response?.data && typeof response.data === "object") {
          setUser((prev) => mergeAccountGateState(prev, response.data));
        }
      } catch {
        // Auth and global error handling are centralized in axios.
      }
    };

    const checkAccountGateStateWhileVisible = () => {
      if (document.visibilityState === "hidden") return;
      checkAccountGateState();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkAccountGateState();
      }
    };

    const timerId = window.setInterval(checkAccountGateStateWhileVisible, 30000);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      cancelled = true;
      window.clearInterval(timerId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loading, user?.id, user?.blocked, user?.legal_reaccept_required]);

  useEffect(() => {
    if (loading || assistantRolloutLoading || user) return;
    if (isNativeApp()) return;
    if (!assistantProductionOnboardingEnabled) return;
    if (
      location.pathname !== "/events/search" &&
      location.pathname !== "/events/map_search"
    ) {
      return;
    }

    let seenAssistantOnboarding = false;
    try {
      seenAssistantOnboarding = localStorage.getItem("assistant_onboarding_seen_v1") === "1";
    } catch {
      seenAssistantOnboarding = false;
    }

    if (seenAssistantOnboarding) return;
    if (backgroundAssistantBootstrapStartedRef.current) return;
    backgroundAssistantBootstrapStartedRef.current = true;

    let cancelled = false;
    let completed = false;
    const timerId = window.setTimeout(async () => {
      try {
        const visitorId = getOrCreateStoredValue("assistant_visitor_id", "visitor");
        const bootstrapSource = getStoredAssistantOnboardingSource() || "landing";
        const storedSessionId =
          typeof window !== "undefined"
            ? String(localStorage.getItem("assistant_session_id") || "").trim()
            : "";
        const response = await api.post(
          "/assistant/bootstrap/",
          {
            session_id: storedSessionId || undefined,
            visitor_id: visitorId,
            source: bootstrapSource,
            language: detectAssistantBootstrapLanguage(language),
            mark_read: false,
          },
          {
            meta: {
              authOptional: true,
              suppressUnauthorizedRedirect: true,
              suppressGlobalError: true,
            },
          }
        );
        if (cancelled) return;

        completed = true;
        const nextSessionId = String(response.data?.session_id || "").trim();
        if (nextSessionId) {
          try {
            localStorage.setItem("assistant_session_id", nextSessionId);
          } catch {
            // ignore
          }
          broadcastAssistantSessionUpdated(nextSessionId);
        }

        if (Boolean(response.data?.meta?.production_onboarding_enabled)) {
          try {
            localStorage.setItem("assistant_onboarding_seen_v1", "1");
          } catch {
            // ignore
          }
        }

        broadcastAssistantUnreadRefresh();
      } catch {
        if (!cancelled) {
          backgroundAssistantBootstrapStartedRef.current = false;
        }
      }
    }, 5000);

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
      if (!completed) {
        backgroundAssistantBootstrapStartedRef.current = false;
      }
    };
  }, [
    assistantProductionOnboardingEnabled,
    assistantRolloutLoading,
    language,
    loading,
    location.pathname,
    user,
  ]);

  // При смене user: если user становится null, отменяем все фоновые запросы и очищаем кэш
  useEffect(() => {
    if (!user) {
      queryClient.cancelQueries();
      queryClient.removeQueries();
      queryClient.clear();
    }
  }, [user]);

  // --- Keys state and logic for search routes ---
  const [keys, setKeys] = useState(null);

  useEffect(() => {
    if (!user || user?.blocked || user?.legal_reaccept_required) {
      setKeys(null);
      return;
    }
    api.get("/storage/user_keys/")
      .then(res => setKeys(res.data))
      .catch(err => console.error("Failed to load keys", err));
  }, [user]);

  const refreshKeys = useCallback(async () => {
    try {
      const res = await api.get("/storage/user_keys/");
      setKeys(res.data);
    } catch (err) {
      console.error("Failed to refresh keys", err);
    }
  }, []);

  const mergeKeys = useCallback(async ({ from, to, count }) => {
    try {
      const res = await api.post("/storage/merge_keys/", { from, to, count });
      const updated = res.data.keys;
      setKeys(prev => ({ ...prev, [from]: updated[from], [to]: updated[to] }));
    } catch (err) {
      console.error("Merge failed", err);
      throw err;
    }
  }, []);

  // on mount, check if session exists
  useEffect(() => {
    api.get("/accounts/profile/", {
      meta: { suppressUnauthorizedRedirect: true, authOptional: true },
    })
      .then(res => {
        const u = res.data;
        const looksAuth =
          u && typeof u === "object" &&
          (Boolean(u?.id) || Boolean(u?.username) || Boolean(u?.email) || u?.is_authenticated === true);

        if (looksAuth) {
          setUser(u);

          // ✅ 1) Язык — главный источник истины после авторизации (берём из БД)
          if (u?.language) {
            try {
              setLanguage(u.language, { persist: true, source: "profile" });
            } catch (e) {
              console.error("Failed to set locale language from profile", e);
            }
          }

          // ✅ 2) Страна — отдельно (может использоваться для UI/регистрации)
          if (u?.country) {
            try {
              setCountry(u.country);
            } catch (e) {
              console.error("Failed to set locale country from profile", e);
            }
          }
        } else {
          setUser(null);
        }
      })
      .catch(err => {
        if (err.response && err.response.status !== 401) {
          console.error("Server error:", err);
        }
        setUser(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setCountry, setLanguage]);

  // On mount: scroll slightly to hide Safari Toolbar
  useEffect(() => {
    window.scrollTo(0, 1);
  }, []);

  const keysCtxValue = useMemo(() => ({ keys, refreshKeys, mergeKeys }), [keys, refreshKeys, mergeKeys]);
  const isGuest = !user || (!user.id && !user.username && !user.email && user?.is_authenticated !== true);
  const isBlockedUser = Boolean(user?.blocked);
  const normalizedPath = (location.pathname || "").toLowerCase();
  const isLegalDocumentPath = normalizedPath.startsWith("/legal/");
  const isImmediatePublicShellPath =
    normalizedPath === "/events" ||
    normalizedPath === "/events/" ||
    normalizedPath.startsWith("/events/search") ||
    normalizedPath.startsWith("/events/map_search") ||
    normalizedPath === "/podujatia-bratislava" ||
    normalizedPath === "/podujatia-bratislava/" ||
    /^\/events\/\d+(?:-[^/?#]+)?\/?$/.test(normalizedPath) ||
    normalizedPath === "/clubs" ||
    normalizedPath === "/clubs/" ||
    /^\/clubs\/\d+(?:-[^/?#]+)?\/?$/.test(normalizedPath) ||
    isOrganizerLandingPath(normalizedPath) ||
    isParticipantLandingPath(normalizedPath);
  const isStandaloneHostGuidePath =
    normalizedPath === "/host-guide" ||
    normalizedPath === "/host-guide/" ||
    normalizedPath === "/host-guide/en" ||
    normalizedPath === "/host-guide/en/" ||
    normalizedPath === "/host-guide/sk" ||
    normalizedPath === "/host-guide/sk/" ||
    normalizedPath === "/host-guide/cs" ||
    normalizedPath === "/host-guide/cs/" ||
    normalizedPath === "/hostguide" ||
    normalizedPath === "/hostguide/" ||
    normalizedPath === "/hostguide/en" ||
    normalizedPath === "/hostguide/en/" ||
    normalizedPath === "/hostguide/sk" ||
    normalizedPath === "/hostguide/sk/" ||
    normalizedPath === "/hostguide/cs" ||
    normalizedPath === "/hostguide/cs/";
  const isStandaloneCommercialConditionsPath =
    normalizedPath === "/commercial-conditions" ||
    normalizedPath === "/commercial-conditions/" ||
    normalizedPath === "/commercial-conditions/en" ||
    normalizedPath === "/commercial-conditions/en/" ||
    normalizedPath === "/commercial-conditions/ru" ||
    normalizedPath === "/commercial-conditions/ru/" ||
    normalizedPath === "/commercial-conditions/sk" ||
    normalizedPath === "/commercial-conditions/sk/";
  const isStandalonePaidHostLegalPath =
    normalizedPath === "/paid-host-verification-terms" ||
    normalizedPath === "/paid-host-verification-terms/" ||
    normalizedPath === "/paid-host-verification-terms/en" ||
    normalizedPath === "/paid-host-verification-terms/en/" ||
    normalizedPath === "/paid-host-verification-terms/ru" ||
    normalizedPath === "/paid-host-verification-terms/ru/" ||
    normalizedPath === "/paid-host-verification-terms/sk" ||
    normalizedPath === "/paid-host-verification-terms/sk/" ||
    normalizedPath === "/paid-host-agreement" ||
    normalizedPath === "/paid-host-agreement/" ||
    normalizedPath === "/paid-host-agreement/en" ||
    normalizedPath === "/paid-host-agreement/en/" ||
    normalizedPath === "/paid-host-agreement/ru" ||
    normalizedPath === "/paid-host-agreement/ru/" ||
    normalizedPath === "/paid-host-agreement/sk" ||
    normalizedPath === "/paid-host-agreement/sk/";
  const isStandalonePaidParticipantLegalPath =
    normalizedPath === "/paid-ticket-participant-terms" ||
    normalizedPath === "/paid-ticket-participant-terms/" ||
    normalizedPath === "/paid-ticket-participant-terms/en" ||
    normalizedPath === "/paid-ticket-participant-terms/en/" ||
    normalizedPath === "/paid-ticket-participant-terms/ru" ||
    normalizedPath === "/paid-ticket-participant-terms/ru/" ||
    normalizedPath === "/paid-ticket-participant-terms/sk" ||
    normalizedPath === "/paid-ticket-participant-terms/sk/";
  const hostGuideLanguage = normalizedPath.includes("/en")
    ? "en"
    : normalizedPath.includes("/cs")
    ? "cs"
    : normalizedPath.includes("/sk")
    ? "sk"
    : undefined;
  const commercialConditionsLanguage = normalizedPath.includes("/ru")
    ? "ru"
    : normalizedPath.includes("/sk")
    ? "sk"
    : normalizedPath.includes("/en")
    ? "en"
    : undefined;
  const paidHostLegalLanguage = normalizedPath.includes("/ru")
    ? "ru"
    : normalizedPath.includes("/sk")
    ? "sk"
    : normalizedPath.includes("/en")
    ? "en"
    : undefined;
  const paidParticipantLegalLanguage = normalizedPath.includes("/ru")
    ? "ru"
    : normalizedPath.includes("/sk")
    ? "sk"
    : normalizedPath.includes("/en")
    ? "en"
    : undefined;
  const paidHostLegalVariant = normalizedPath.includes("/paid-host-verification-terms")
    ? "verification"
    : "agreement";

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (!(isStandaloneHostGuidePath || isStandaloneCommercialConditionsPath || isStandalonePaidHostLegalPath || isStandalonePaidParticipantLegalPath)) {
      return undefined;
    }
    document.body.classList.add("organizer-panel-scroll-edge");
    return () => {
      document.body.classList.remove("organizer-panel-scroll-edge");
    };
  }, [isStandaloneCommercialConditionsPath, isStandaloneHostGuidePath, isStandalonePaidHostLegalPath, isStandalonePaidParticipantLegalPath]);

  const handleCloseHostClaim = useCallback(() => {
    const tokenFromUrl = new URLSearchParams(location.search).get("host_claim");
    dismissedHostClaimTokenRef.current = hostClaimToken || tokenFromUrl || null;
    setHostClaimOpen(false);
    setHostClaimToken(null);
    if (tokenFromUrl) {
      navigate(removeAttendanceClaimFromLocation(location), { replace: true });
    }
  }, [hostClaimToken, location, navigate]);

  if (normalizedPath === "/menu" || normalizedPath === "/menu/main") {
    return <Navigate to={`/vault/main${location.search || ""}`} replace />;
  }

  if (isOrganizerLandingPath(normalizedPath)) {
    const landingLanguage = getCommunityLandingLanguage(normalizedPath);
    if (!landingLanguage) return <Navigate to="/for-organizers/sk" replace />;
    return <OrganizerLanding isGuest={isGuest} language={landingLanguage} />;
  }

  if (isParticipantLandingPath(normalizedPath)) {
    const landingLanguage = getCommunityLandingLanguage(normalizedPath);
    if (!landingLanguage) return <Navigate to="/for-participants/sk" replace />;
    return <ParticipantLanding isGuest={isGuest} language={landingLanguage} />;
  }

  if (isStandaloneHostGuidePath) {
    return (
      <QueryClientProvider client={queryClient}>
        <AssistantUnreadProvider>
          <RouterGuard>
            <AppShell
              isGuest={isGuest}
              bottomTheme="dark"
              sideTheme="dark"
              innerFactor={1.0}
              disableMaxWidth
              disableMobileBottomPadding
            >
              <HostGuide forcedLanguageCode={hostGuideLanguage} isGuest={isGuest} />
            </AppShell>
            <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[10000] hidden justify-center px-4 pb-4 pt-2 lg:flex">
              <CookieConsentBanner isDesktop />
            </div>
            <div
              className="pointer-events-none fixed inset-x-0 bottom-0 z-[10000] flex justify-center px-3 pb-[calc(env(safe-area-inset-bottom,0px)+4px)] pt-2 lg:hidden"
            >
              <CookieConsentBanner />
            </div>
            <AssistantNotificationToast />
          </RouterGuard>
        </AssistantUnreadProvider>
      </QueryClientProvider>
    );
  }

  if (isStandaloneCommercialConditionsPath) {
    return (
      <QueryClientProvider client={queryClient}>
        <AssistantUnreadProvider>
          <RouterGuard>
            <AppShell
              isGuest={isGuest}
              bottomTheme="dark"
              sideTheme="dark"
              innerFactor={1.0}
              disableMaxWidth
              disableMobileBottomPadding
            >
              <CommercialConditionsPage
                forcedLanguageCode={commercialConditionsLanguage}
                isGuest={isGuest}
              />
            </AppShell>
            <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[10000] hidden justify-center px-4 pb-4 pt-2 lg:flex">
              <CookieConsentBanner isDesktop />
            </div>
            <div
              className="pointer-events-none fixed inset-x-0 bottom-0 z-[10000] flex justify-center px-3 pb-[calc(env(safe-area-inset-bottom,0px)+4px)] pt-2 lg:hidden"
            >
              <CookieConsentBanner />
            </div>
            <AssistantNotificationToast />
          </RouterGuard>
        </AssistantUnreadProvider>
      </QueryClientProvider>
    );
  }

  if (isStandalonePaidHostLegalPath) {
    return (
      <QueryClientProvider client={queryClient}>
        <AssistantUnreadProvider>
          <RouterGuard>
            <PaidHostLegalPage
              variant={paidHostLegalVariant}
              forcedLanguageCode={paidHostLegalLanguage}
            />
            <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[10000] hidden justify-center px-4 pb-4 pt-2 lg:flex">
              <CookieConsentBanner isDesktop />
            </div>
            <div
              className="pointer-events-none fixed inset-x-0 bottom-0 z-[10000] flex justify-center px-3 pb-[calc(env(safe-area-inset-bottom,0px)+4px)] pt-2 lg:hidden"
            >
              <CookieConsentBanner />
            </div>
            <AssistantNotificationToast />
          </RouterGuard>
        </AssistantUnreadProvider>
      </QueryClientProvider>
    );
  }

  if (isStandalonePaidParticipantLegalPath) {
    return (
      <QueryClientProvider client={queryClient}>
        <AssistantUnreadProvider>
          <RouterGuard>
            <PaidTicketParticipantTermsPage
              forcedLanguageCode={paidParticipantLegalLanguage}
            />
            <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[10000] hidden justify-center px-4 pb-4 pt-2 lg:flex">
              <CookieConsentBanner isDesktop />
            </div>
            <div
              className="pointer-events-none fixed inset-x-0 bottom-0 z-[10000] flex justify-center px-3 pb-[calc(env(safe-area-inset-bottom,0px)+4px)] pt-2 lg:hidden"
            >
              <CookieConsentBanner />
            </div>
            <AssistantNotificationToast />
          </RouterGuard>
        </AssistantUnreadProvider>
      </QueryClientProvider>
    );
  }

  if (isLegalDocumentPath) {
    const legalElement = (content) => (
      <AppShell
        isGuest={isGuest}
        bottomTheme="dark"
        sideTheme="dark"
        disableMaxWidth
        disableMobileBottomPadding
        hideBottomNavigation
      >
        {content}
      </AppShell>
    );
    return (
      <QueryClientProvider client={queryClient}>
        <RouterGuard>
          <Routes location={location}>
            <Route path="/legal/terms" element={legalElement(<UserAgreement />)} />
            <Route path="/legal/user-agreement" element={legalElement(<RussianUserAgreement />)} />
            <Route path="/legal/community-rules" element={legalElement(<CommunityRules />)} />
            <Route path="/legal/moderation" element={legalElement(<ModerationPolicy />)} />
            <Route path="/legal/privacy" element={legalElement(<PrivacyPolicy />)} />
            <Route path="/legal/personal-data-consent" element={legalElement(<PersonalDataConsent />)} />
            <Route path="/legal/cookies" element={legalElement(<CookiesPolicy />)} />
            <Route path="/legal/child-safety" element={legalElement(<ChildSafetyStandards />)} />
            <Route path="*" element={<Navigate to="/legal/terms" replace />} />
          </Routes>
        </RouterGuard>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <React.Suspense fallback={null}>
      <AssistantUnreadProvider>
      <ProfileCardProvider isGuest={isGuest}>
      <RouterGuard>
        {!loading && isBlockedUser && !isLegalDocumentPath ? (
          <BlockedAccountScreen
            blockReason={user?.block_reason}
            decision={user?.moderation_decision}
          />
        ) : !loading && user?.legal_reaccept_required && !isLegalDocumentPath ? (
          <LegalAcceptanceGate
            onAccepted={() => {
              setUser((current) => (
                current ? { ...current, legal_reaccept_required: false } : current
              ));
            }}
          />
        ) : location.pathname === "/" ||
        location.pathname === "/auth" ||
        location.pathname === "/info" ||
        location.pathname === "/silent-disco" ||
        location.pathname === "/about" ||
        (INCLUDE_MARKETING_ROUTES && isOrganizerLandingPath(location.pathname)) ||
        (INCLUDE_MARKETING_ROUTES && isParticipantLandingPath(location.pathname)) ||
        (INCLUDE_MARKETING_ROUTES && location.pathname === "/podujatia-bratislava") ||
        (INCLUDE_MARKETING_ROUTES && location.pathname === "/podujatia-bratislava/") ||
        (internalToolsAvailable && location.pathname === "/roulette") ||
        location.pathname.startsWith("/partner-place-map-picker") ||
        location.pathname.startsWith("/about/") ||
        location.pathname.startsWith("/accounts/confirm-email") ||
        location.pathname.startsWith("/ig-gate") ||
        (loading && isImmediatePublicShellPath) ? (
          <Routes>
            <Route path="/ig-gate" element={<IGGate />} />
            <Route path="/" element={<RootRoute user={user} loading={loading} />} />
            {INCLUDE_MARKETING_ROUTES ? (
              <Route
                path="/podujatia-bratislava"
                element={<BratislavaEventsLanding isGuest={isGuest} />}
              />
            ) : null}
            {INCLUDE_MARKETING_ROUTES ? (
              <>
                <Route
                  path="/for-organizers"
                  element={<Navigate to="/for-organizers/sk" replace />}
                />
                <Route
                  path="/organizers"
                  element={<Navigate to="/for-organizers/sk" replace />}
                />
                <Route path="/for-organizers/en" element={<OrganizerLanding isGuest={isGuest} language="en" />} />
                <Route path="/for-organizers/sk" element={<OrganizerLanding isGuest={isGuest} language="sk" />} />
                <Route path="/for-organizers/cs" element={<OrganizerLanding isGuest={isGuest} language="cs" />} />
                <Route path="/organizers/en" element={<OrganizerLanding isGuest={isGuest} language="en" />} />
                <Route path="/organizers/sk" element={<OrganizerLanding isGuest={isGuest} language="sk" />} />
                <Route path="/organizers/cs" element={<OrganizerLanding isGuest={isGuest} language="cs" />} />
                <Route path="/for-organizers/" element={<Navigate to="/for-organizers" replace />} />
                <Route path="/organizers/" element={<Navigate to="/for-organizers" replace />} />
              </>
            ) : null}
            {INCLUDE_MARKETING_ROUTES ? (
              <>
                <Route
                  path="/for-participants"
                  element={<Navigate to="/for-participants/sk" replace />}
                />
                <Route
                  path="/participants"
                  element={<Navigate to="/for-participants/sk" replace />}
                />
                <Route path="/for-participants/en" element={<ParticipantLanding isGuest={isGuest} language="en" />} />
                <Route path="/for-participants/sk" element={<ParticipantLanding isGuest={isGuest} language="sk" />} />
                <Route path="/for-participants/cs" element={<ParticipantLanding isGuest={isGuest} language="cs" />} />
                <Route path="/participants/en" element={<ParticipantLanding isGuest={isGuest} language="en" />} />
                <Route path="/participants/sk" element={<ParticipantLanding isGuest={isGuest} language="sk" />} />
                <Route path="/participants/cs" element={<ParticipantLanding isGuest={isGuest} language="cs" />} />
                <Route path="/for-participants/" element={<Navigate to="/for-participants" replace />} />
                <Route path="/participants/" element={<Navigate to="/participants" replace />} />
              </>
            ) : null}
            {/* Roulette admin — standalone, web-only, auth guard inside component */}
            {internalToolsAvailable ? (
              <Route path="/roulette" element={<RouletteAdminPage />} />
            ) : null}
            <Route path="/partner-place-map-picker/*" element={<PartnerPlaceMapPickerPage />} />
            <Route
              path="/silent-disco"
              element={
                <AppShell
                  isGuest={isGuest}
                  bottomTheme="dark"
                  sideTheme="dark"
                  innerFactor={0.6}
                >
                  <SilentDiscoPage />
                </AppShell>
              }
            />
            <Route
              path="/about"
              element={<AboutPage />}
            />
            <Route
              path="/about/tap-rules"
              element={<AboutPage />}
            />
            <Route
              path="/clubs"
              element={
                <AppShell
                  isGuest={isGuest}
                  bottomTheme="dark"
                  sideTheme="dark"
                  innerFactor={0.6}
                  disableMobileBottomPadding
                >
                  <ClubsPage isGuest={isGuest} />
                </AppShell>
              }
            />
            <Route
              path="/clubs/:id"
              element={
                <AppShell
                  isGuest={isGuest}
                  bottomTheme="dark"
                  sideTheme="dark"
                  innerFactor={0.6}
                  disableMobileBottomPadding
                >
                  <ClubPublicPage isGuest={isGuest} />
                </AppShell>
              }
            />
            <Route path="/events" element={<Navigate to="/events/search" replace />} />
            <Route
              path="/events/search"
              element={
                <AppShell
                  isGuest={isGuest}
                  bottomTheme="dark"
                  sideTheme="dark"
                  innerFactor={0.6}
                  disableMobileBottomPadding
                >
                  <ListSearch isGuest={isGuest} userId={user?.id} authLoading={loading} />
                </AppShell>
              }
            />
            <Route
              path="/events/map_search"
              element={
                <AppShell
                  isGuest={isGuest}
                  bottomTheme="dark"
                  sideTheme="dark"
                  innerFactor={1.0}
                  disableMaxWidth
                  disableMobileBottomPadding
                >
                  <MapSearch isGuest={isGuest} />
                </AppShell>
              }
            />
            <Route path="/events/:id/buy/*" element={<EventBuyRedirect />} />
            <Route path="/events/:id/gallery" element={<AuthRedirect />} />
            <Route path="/events/:id" element={<EventPublicPage isGuest={isGuest} />} />
            <Route
              path="/auth"
              element={
                <AppShell
                  isGuest={isGuest}
                  bottomTheme="dark"
                  sideTheme="dark"
                  innerFactor={1.0}
                  disableMaxWidth
                  disableMobileBottomPadding
                  hideBottomNavigation
                >
                  <AuthPage
                    onAuth={(user) => {
                      setUser(user);

                      // ✅ После авторизации язык берём из профиля (БД)
                      if (user?.language) {
                        try {
                          setLanguage(user.language, { persist: true, source: "profile" });
                        } catch (e) {
                          console.error("Failed to set locale language from auth profile", e);
                        }
                      }

                      // 👇 Страна — отдельно
                      if (user?.country) {
                        try {
                          setCountry(user.country);
                        } catch (e) {
                          console.error("Failed to set locale country from auth profile", e);
                        }
                      }

                      linkTelegramAccount();
                    }}
                  />
                </AppShell>
              }
            />
            <Route
              path="/accounts/confirm-email/:uid/:token/"
              element={<EmailConfirmPage />}
            />
            <Route path="/info" element={<RevolutVerification />} />
          </Routes>
        ) : (
          loading ? (
            null
          ) : user ? (
            <AppStateProvider userId={user.id}>
              <TasksProvider>
              <MailboxProvider>
                <ChatUnreadProvider>
                  <NativeBadgeSync />
                  <ChatRealtimeProvider>
                  <StatisticsUnreadProvider>
                    <EventHostAttentionProvider userId={user.id}>
                    <WaitlistUnreadProvider>
                    <ParticipantRequestUnreadProvider>
                    <HostFeedbackUnreadProvider>
                    <ClubAttentionProvider>
                    <ClubMemberUnreadProvider>
                    <ClubRequestUnreadProvider>
                    <ParticipantRatingUnreadProvider>
                    <MutualUnreadProvider>
                    <CastleSkinProvider>
                      <KeysContext.Provider value={keysCtxValue}>
                        <EvaluationProvider user={user}>
                          <RequiredActionsProvider
                            user={user}
                            currentPathname={location.pathname}
                          >
                            <CheckinProvider>
                              <CityLaunchProvider key={`${user.id}:${user.country || ""}:${user.city || ""}`}>
                              <ShopModeProvider>
                              <GlobalPhotoGuard user={user} />
                              <Routes location={guideBackgroundLocation}>
                          <Route path="/ig-gate" element={<IGGate />} />
                          <Route
                            path="/legal/terms"
                            element={
                              <AppShell
                                isGuest={isGuest}
                                bottomTheme="dark"
                                sideTheme="dark"
                                disableMaxWidth
                                disableMobileBottomPadding
                                hideBottomNavigation
                              >
                                <UserAgreement />
                              </AppShell>
                            }
                          />
                          <Route
                            path="/legal/privacy"
                            element={
                              <AppShell
                                isGuest={isGuest}
                                bottomTheme="dark"
                                sideTheme="dark"
                                disableMaxWidth
                                disableMobileBottomPadding
                                hideBottomNavigation
                              >
                                <PrivacyPolicy />
                              </AppShell>
                            }
                          />
                          <Route
                            path="/legal/community-rules"
                            element={
                              <AppShell
                                isGuest={isGuest}
                                bottomTheme="dark"
                                sideTheme="dark"
                                disableMaxWidth
                                disableMobileBottomPadding
                                hideBottomNavigation
                              >
                                <CommunityRules />
                              </AppShell>
                            }
                          />
                          <Route
                            path="/legal/moderation"
                            element={
                              <AppShell
                                isGuest={isGuest}
                                bottomTheme="dark"
                                sideTheme="dark"
                                disableMaxWidth
                                disableMobileBottomPadding
                                hideBottomNavigation
                              >
                                <ModerationPolicy />
                              </AppShell>
                            }
                          />
                          <Route
                            path="/legal/cookies"
                            element={
                              <AppShell
                                isGuest={isGuest}
                                bottomTheme="dark"
                                sideTheme="dark"
                                disableMaxWidth
                                disableMobileBottomPadding
                                hideBottomNavigation
                              >
                                <CookiesPolicy />
                              </AppShell>
                            }
                          />
                          <Route
                            path="/legal/child-safety"
                            element={
                              <AppShell
                                isGuest={isGuest}
                                bottomTheme="dark"
                                sideTheme="dark"
                                disableMaxWidth
                                disableMobileBottomPadding
                                hideBottomNavigation
                              >
                                <ChildSafetyStandards />
                              </AppShell>
                            }
                          />
                          <Route
                            path="/account-deletion"
                            element={
                              <AppShell
                                isGuest={isGuest}
                                bottomTheme="dark"
                                sideTheme="dark"
                                disableMaxWidth
                                disableMobileBottomPadding
                                hideBottomNavigation
                              >
                                <AccountDeletionPage />
                              </AppShell>
                            }
                          />
                          <Route
                            path="/assistant/core"
                            element={
                              <AppShell
                                isGuest={isGuest}
                                bottomTheme="dark"
                                sideTheme="dark"
                                innerFactor={0.6}
                                disableMobileBottomPadding
                              >
                                <AssistantCorePage isGuest={isGuest} />
                              </AppShell>
                            }
                          />
                          {/* protected routes below */}
                          <Route
                            path="/shop/item"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <ShopShell>
                                  <ShopItem />
                                </ShopShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/shop/cookies"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <ShopShell>
                                  <ShopItem />
                                </ShopShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/shop/cart"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <ShopShell>
                                  <Cart />
                                </ShopShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/shop/garment"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <ShopShell>
                                  <Garment />
                                </ShopShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/shop/web3"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <ShopShell>
                                  <Web3 />
                                </ShopShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/shop/catalog"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <ShopShell>
                                  <Catalog />
                                </ShopShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/shop/delivery"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <ShopShell>
                                  <ShopDelivery />
                                </ShopShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/shop/contact"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <ShopShell>
                                  <Contact />
                                </ShopShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/clubs"
                            element={
                              <AppShell
                                isGuest={isGuest}
                                bottomTheme="dark"
                                sideTheme="dark"
                                innerFactor={0.6}
                                disableMobileBottomPadding
                              >
                                <ClubsPage isGuest={isGuest} />
                              </AppShell>
                            }
                          />
                          <Route
                            path="/menu/clubs"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={1.0}
                                  disableMaxWidth
                                  disableMobileBottomPadding
                                >
                                  <ClubsPage isGuest={isGuest} />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/clubs/create"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={1.0}
                                  disableMaxWidth
                                  hideBottomNavigation
                                  disableMobileBottomPadding
                                  allowScrollWhenBottomNavHidden
                                >
                                  <ClubCreatePage />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/clubs/:id/edit"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={1.0}
                                  disableMaxWidth
                                  hideBottomNavigation
                                  disableMobileBottomPadding
                                  allowScrollWhenBottomNavHidden
                                >
                                  <ClubCreatePage />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/clubs/:id/manage"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={1.0}
                                  disableMaxWidth
                                  disableMobileBottomPadding
                                >
                                  <ClubManagePage />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/clubs/:id/manage/ownership"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={1.0}
                                  disableMaxWidth
                                  disableMobileBottomPadding
                                >
                                  <ClubOwnershipPage />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/clubs/:id/manage/gallery"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={1.0}
                                  disableMaxWidth
                                  hideBottomNavigation
                                  disableMobileBottomPadding
                                  allowScrollWhenBottomNavHidden
                                >
                                  <ClubGalleryManagePage />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          {internalToolsAvailable ? (
                            <Route
                              path="/staff/media-library"
                              element={
                                <PrivateRoute user={user} loading={loading}>
                                  <AppShell
                                    isGuest={isGuest}
                                    bottomTheme="dark"
                                    sideTheme="dark"
                                    innerFactor={1.0}
                                    disableMaxWidth
                                    hideBottomNavigation
                                    disableMobileBottomPadding
                                    allowScrollWhenBottomNavHidden
                                  >
                                    <StaffMediaLibraryPage user={user} />
                                  </AppShell>
                                </PrivateRoute>
                              }
                            />
                          ) : null}
                          <Route
                            path="/clubs/:id/manage/requests"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={1.0}
                                  disableMaxWidth
                                  disableMobileBottomPadding
                                >
                                  <ClubRequestsPage />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/clubs/:id/manage/members"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={1.0}
                                  disableMaxWidth
                                  disableMobileBottomPadding
                                >
                                  <ClubMembersPage />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/clubs/:id"
                            element={
                              <AppShell
                                isGuest={isGuest}
                                bottomTheme="dark"
                                sideTheme="dark"
                                innerFactor={0.6}
                                disableMobileBottomPadding
                              >
                                <ClubPublicPage isGuest={isGuest} />
                              </AppShell>
                            }
                          />
                          <Route
                            path="/events"
                            element={<Navigate to="/events/search" replace />}
                          />
                          <Route
                            path="/events/search"
                            element={
                              <AppShell
                                isGuest={isGuest}
                                bottomTheme="dark"
                                sideTheme="dark"
                                innerFactor={0.6}
                                disableMobileBottomPadding
                              >
                                <ListSearch isGuest={isGuest} userId={user?.id} authLoading={loading} />
                              </AppShell>
                            }
                          />
                          <Route
                            path="/events/map_search"
                            element={
                              <AppShell isGuest={isGuest} bottomTheme="dark" sideTheme="dark" innerFactor={1.0} disableMaxWidth disableMobileBottomPadding>
                                <MapSearch isGuest={isGuest} />
                              </AppShell>
                            }
                          />
                          <Route
                            path="/events/participant-rating"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={1.0}
                                  disableMaxWidth
                                  disableMobileBottomPadding
                                >
                                  <div className="h-full w-full bg-[#112039]">
                                    <ParticipantRatingPage mode="rating" />
                                  </div>
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/events/my-tickets"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={1.0}
                                  disableMaxWidth
                                  disableMobileBottomPadding
                                >
                                  <div className="h-full w-full bg-[#112039]">
                                    <ParticipantRatingPage mode="tickets" />
                                  </div>
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          {localDebugRoutesEnabled ? (
                            <Route
                              path="/local/events/leave-sandbox"
                              element={
                                <PrivateRoute user={user} loading={loading}>
                                  <AppShell
                                    isGuest={isGuest}
                                    bottomTheme="dark"
                                    sideTheme="dark"
                                    innerFactor={1.0}
                                    disableMaxWidth
                                    disableMobileBottomPadding
                                  >
                                    <LeaveFlowSandboxPage />
                                  </AppShell>
                                </PrivateRoute>
                              }
                            />
                          ) : null}
                      {/* Modal sandbox: web local dev OR staff/superuser on production */}
                      {internalToolsAvailable ? (
                        <Route
                          path="/local-debug/modal-sandbox"
                          element={
                            localDebugRoutesEnabled ? (
                              <AppShell
                                isGuest={isGuest}
                                bottomTheme="dark"
                                sideTheme="dark"
                                innerFactor={1.0}
                                disableMaxWidth
                                disableMobileBottomPadding
                              >
                                <ModalSandboxPage user={user} />
                              </AppShell>
                            ) : (
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={1.0}
                                  disableMaxWidth
                                  disableMobileBottomPadding
                                >
                                  <ModalSandboxPage user={user} />
                                </AppShell>
                              </PrivateRoute>
                            )
                          }
                        />
                      ) : null}
                      {localDebugRoutesEnabled ? (
                        <Route
                          path="/local-debug/onboarding-sandbox"
                          element={
                            <PrivateRoute user={user} loading={loading}>
                              <AppShell
                                isGuest={isGuest}
                                bottomTheme="dark"
                                sideTheme="dark"
                                innerFactor={1.0}
                                disableMaxWidth
                                disableMobileBottomPadding
                              >
                                <OnboardingSandboxPage />
                              </AppShell>
                            </PrivateRoute>
                          }
                        />
                      ) : null}
                      {localDebugRoutesEnabled ? (
                        <Route
                          path="/local-debug/paid-event-creation"
                          element={
                            <AppShell
                              isGuest
                              bottomTheme="dark"
                              sideTheme="dark"
                              innerFactor={1.0}
                              disableMaxWidth
                              disableMobileBottomPadding
                              hideBottomNavigation
                            >
                              <EventCreateForm sandboxMode />
                            </AppShell>
                          }
                        />
                      ) : null}
                      {localDebugRoutesEnabled ? (
                        <Route
                          path="/local-debug/publication-city-sandbox"
                          element={<AppShell isGuest bottomTheme="dark" sideTheme="dark" innerFactor={1.0} disableMaxWidth disableMobileBottomPadding hideBottomNavigation><PublicationCitySandboxPage /></AppShell>}
                        />
                      ) : null}
                      {localDebugRoutesEnabled ? (
                        <Route
                          path="/local-debug/registration-onboarding"
                          element={
                            <AppShell
                              isGuest
                              bottomTheme="dark"
                              sideTheme="dark"
                              innerFactor={1.0}
                              disableMaxWidth
                              disableMobileBottomPadding
                              hideBottomNavigation
                            >
                              <AuthPage
                                registrationOnboardingFlowPreview
                                onAuth={() => {}}
                              />
                            </AppShell>
                          }
                        />
                      ) : null}
                      {localDebugRoutesEnabled ? (
                        <Route
                          path="/local-debug/registration-onboarding/cards"
                          element={<RegistrationOnboardingPreviewPage user={user} />}
                        />
                      ) : null}
                      {localDebugRoutesEnabled ? (
                        <Route path="/local-debug/vault-guide" element={<VaultGuidePlatformPage user={user} isGuest={isGuest} />} />
                      ) : null}
                      <Route
                        path="/guide"
                        element={<PrivateRoute user={user} loading={loading}><VaultGuidePlatformPage user={user} isGuest={isGuest} /></PrivateRoute>}
                      />
                      {localDebugRoutesEnabled ? (
                        <Route path="/local-debug/subscription-payment" element={<SubscriptionPaymentPreviewPage />} />
                      ) : null}
                      <Route
                        path="/guide"
                        element={<PrivateRoute user={user} loading={loading}><VaultGuidePlatformPage user={user} isGuest={isGuest} /></PrivateRoute>}
                      />
                      {localDebugRoutesEnabled ? (
                        <Route path="/local-debug/key-selector" element={<KeySelectorPreviewPage />} />
                      ) : null}
                      <Route
                        path="/registration/onboarding"
                        element={(
                          <PrivateRoute user={user} loading={loading}>
                            <RegistrationOnboardingPreviewPage user={user} />
                          </PrivateRoute>
                        )}
                      />
                          {localDebugRoutesEnabled ? (
                            <Route
                              path="/local-debug/invoice-preview"
                              element={
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={1.0}
                                  disableMaxWidth
                                  disableMobileBottomPadding
                                >
                                  <InvoicePreviewPage />
                                </AppShell>
                              }
                            />
                          ) : null}
                          <Route
                            path="/events/hosting-rules"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={1.0}
                                  disableMaxWidth
                                  hideBottomNavigation
                                  disableMobileBottomPadding
                                >
                                  <HostRulesPage />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/events/create"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={1.0}
                                  disableMaxWidth
                                  hideBottomNavigation
                                  disableMobileBottomPadding
                                >
                                  <EventCreateForm
                                    key={new URLSearchParams(location.search).get("shop_payment") || new URLSearchParams(location.search).get("start_new") || "new-event"}
                                    userId={user?.id}
                                  />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/events/edit"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={1.0}
                                  disableMaxWidth
                                  hideBottomNavigation
                                  disableMobileBottomPadding
                                >
                                  <EventCreateForm userId={user?.id} />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/events/:id/buy/*"
                            element={<EventBuyRedirect />}
                          />
                          <Route
                            path="/events/:id/gallery"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={1.0}
                                  disableMaxWidth
                                  disableMobileBottomPadding
                                  hideBottomNavigation
                                >
                                  <EventGalleryPage user={user} />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/events/:id"
                            element={<EventPublicPage isGuest={isGuest} />}
                          />
                          <Route
                            path="/events/vote/:token"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell isGuest={isGuest} bottomTheme="dark" sideTheme="dark" innerFactor={0.6}>
                                  <PartyGamesVote />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/host-guide"
                            element={
                              <AppShell
                                isGuest={isGuest}
                                bottomTheme="dark"
                                sideTheme="dark"
                                innerFactor={0.6}
                                disableMobileBottomPadding
                              >
                                <HostGuide isGuest={isGuest} />
                              </AppShell>
                            }
                          />
                          <Route
                            path="/commercial-conditions"
                            element={
                              <AppShell
                                isGuest={isGuest}
                                bottomTheme="dark"
                                sideTheme="dark"
                                innerFactor={0.72}
                                disableMobileBottomPadding
                              >
                                <CommercialConditionsPage isGuest={isGuest} />
                              </AppShell>
                            }
                          />
                          <Route
                            path="/commercial-conditions/en"
                            element={
                              <AppShell
                                isGuest={isGuest}
                                bottomTheme="dark"
                                sideTheme="dark"
                                innerFactor={0.72}
                                disableMobileBottomPadding
                              >
                                <CommercialConditionsPage forcedLanguageCode="en" isGuest={isGuest} />
                              </AppShell>
                            }
                          />
                          <Route
                            path="/commercial-conditions/ru"
                            element={
                              <AppShell
                                isGuest={isGuest}
                                bottomTheme="dark"
                                sideTheme="dark"
                                innerFactor={0.72}
                                disableMobileBottomPadding
                              >
                                <CommercialConditionsPage forcedLanguageCode="ru" isGuest={isGuest} />
                              </AppShell>
                            }
                          />
                          <Route
                            path="/commercial-conditions/sk"
                            element={
                              <AppShell
                                isGuest={isGuest}
                                bottomTheme="dark"
                                sideTheme="dark"
                                innerFactor={0.72}
                                disableMobileBottomPadding
                              >
                                <CommercialConditionsPage forcedLanguageCode="sk" isGuest={isGuest} />
                              </AppShell>
                            }
                          />
                          <Route
                            path="/paid-host-verification-terms"
                            element={<PaidHostLegalPage variant="verification" />}
                          />
                          <Route
                            path="/paid-host-verification-terms/en"
                            element={<PaidHostLegalPage variant="verification" forcedLanguageCode="en" />}
                          />
                          <Route
                            path="/paid-host-verification-terms/ru"
                            element={<PaidHostLegalPage variant="verification" forcedLanguageCode="ru" />}
                          />
                          <Route
                            path="/paid-host-verification-terms/sk"
                            element={<PaidHostLegalPage variant="verification" forcedLanguageCode="sk" />}
                          />
                          <Route
                            path="/paid-host-agreement"
                            element={<PaidHostLegalPage variant="agreement" />}
                          />
                          <Route
                            path="/paid-host-agreement/en"
                            element={<PaidHostLegalPage variant="agreement" forcedLanguageCode="en" />}
                          />
                          <Route
                            path="/paid-host-agreement/ru"
                            element={<PaidHostLegalPage variant="agreement" forcedLanguageCode="ru" />}
                          />
                          <Route
                            path="/paid-host-agreement/sk"
                            element={<PaidHostLegalPage variant="agreement" forcedLanguageCode="sk" />}
                          />
                          <Route
                            path="/paid-ticket-participant-terms"
                            element={<PaidTicketParticipantTermsPage />}
                          />
                          <Route
                            path="/paid-ticket-participant-terms/en"
                            element={<PaidTicketParticipantTermsPage forcedLanguageCode="en" />}
                          />
                          <Route
                            path="/paid-ticket-participant-terms/ru"
                            element={<PaidTicketParticipantTermsPage forcedLanguageCode="ru" />}
                          />
                          <Route
                            path="/paid-ticket-participant-terms/sk"
                            element={<PaidTicketParticipantTermsPage forcedLanguageCode="sk" />}
                          />
                          <Route
                            path="/host-guide/en"
                            element={
                              <AppShell
                                isGuest={isGuest}
                                bottomTheme="dark"
                                sideTheme="dark"
                                innerFactor={0.6}
                                disableMobileBottomPadding
                              >
                                <HostGuide forcedLanguageCode="en" isGuest={isGuest} />
                              </AppShell>
                            }
                          />
                          <Route
                            path="/host-guide/sk"
                            element={
                              <AppShell
                                isGuest={isGuest}
                                bottomTheme="dark"
                                sideTheme="dark"
                                innerFactor={0.6}
                                disableMobileBottomPadding
                              >
                                <HostGuide forcedLanguageCode="sk" isGuest={isGuest} />
                              </AppShell>
                            }
                          />
                          <Route
                            path="/host-guide/cs"
                            element={
                              <AppShell
                                isGuest={isGuest}
                                bottomTheme="dark"
                                sideTheme="dark"
                                innerFactor={0.6}
                                disableMobileBottomPadding
                              >
                                <HostGuide forcedLanguageCode="cs" isGuest={isGuest} />
                              </AppShell>
                            }
                          />
                          <Route path="/hostguide" element={<Navigate to="/host-guide" replace />} />
                          <Route path="/hostguide/en" element={<Navigate to="/host-guide/en" replace />} />
                          <Route path="/hostguide/sk" element={<Navigate to="/host-guide/sk" replace />} />
                      <Route path="/hostguide/cs" element={<Navigate to="/host-guide/cs" replace />} />
                          <Route path="/host-guide/" element={<Navigate to="/host-guide" replace />} />
                          <Route path="/host-guide/en/" element={<Navigate to="/host-guide/en" replace />} />
                          <Route path="/host-guide/sk/" element={<Navigate to="/host-guide/sk" replace />} />
                      <Route path="/host-guide/cs/" element={<Navigate to="/host-guide/cs" replace />} />
                          <Route path="/commercial-conditions/" element={<Navigate to="/commercial-conditions" replace />} />
                          <Route path="/commercial-conditions/en/" element={<Navigate to="/commercial-conditions/en" replace />} />
                          <Route path="/commercial-conditions/ru/" element={<Navigate to="/commercial-conditions/ru" replace />} />
                          <Route path="/commercial-conditions/sk/" element={<Navigate to="/commercial-conditions/sk" replace />} />
                          <Route path="/paid-host-verification-terms/" element={<Navigate to="/paid-host-verification-terms" replace />} />
                          <Route path="/paid-host-verification-terms/en/" element={<Navigate to="/paid-host-verification-terms/en" replace />} />
                          <Route path="/paid-host-verification-terms/ru/" element={<Navigate to="/paid-host-verification-terms/ru" replace />} />
                          <Route path="/paid-host-verification-terms/sk/" element={<Navigate to="/paid-host-verification-terms/sk" replace />} />
                          <Route path="/paid-host-agreement/" element={<Navigate to="/paid-host-agreement" replace />} />
                          <Route path="/paid-host-agreement/en/" element={<Navigate to="/paid-host-agreement/en" replace />} />
                          <Route path="/paid-host-agreement/ru/" element={<Navigate to="/paid-host-agreement/ru" replace />} />
                          <Route path="/paid-host-agreement/sk/" element={<Navigate to="/paid-host-agreement/sk" replace />} />
                          <Route path="/paid-ticket-participant-terms/" element={<Navigate to="/paid-ticket-participant-terms" replace />} />
                          <Route path="/paid-ticket-participant-terms/en/" element={<Navigate to="/paid-ticket-participant-terms/en" replace />} />
                          <Route path="/paid-ticket-participant-terms/ru/" element={<Navigate to="/paid-ticket-participant-terms/ru" replace />} />
                          <Route path="/paid-ticket-participant-terms/sk/" element={<Navigate to="/paid-ticket-participant-terms/sk" replace />} />
                          <Route
                            path="/menu/chats"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={1}
                                  disableMaxWidth
                                  disableMobileBottomPadding
                                >
                                  <ChatsPage />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/menu/mailbox"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={1}
                                  disableMaxWidth
                                  disableMobileBottomPadding
                                >
                                  <MailboxPage />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/menu/singles"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={1}
                                  disableMaxWidth
                                  disableMobileBottomPadding
                                >
                                  <SinglesPage setUser={setUser} />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/menu/dinners"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={1}
                                  disableMaxWidth
                                  disableMobileBottomPadding
                                  showBottomNavScrim={false}
                                >
                                  <DinnersPage />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/menu/profile"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <Navigate to="/vault/main?open_manage_vault=1" replace />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/menu/subscription"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={1}
                                  disableMaxWidth
                                  disableMobileBottomPadding
                                >
                                  <PremiumRouteTransition>
                                    <SubscriptionConceptPage />
                                  </PremiumRouteTransition>
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/menu/subscription-concept"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <Navigate to="/menu/subscription" replace />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/menu/badge"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <BadgePage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/menu/token"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell isGuest={isGuest} bottomTheme="dark" sideTheme="dark" innerFactor={0.6}>
                                  <TokenPage />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/menu/contact"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell isGuest={isGuest} bottomTheme="dark" sideTheme="dark" innerFactor={0.6}>
                                  <HelpPage />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/menu/chats/event/:eventId"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={1}
                                  disableMaxWidth
                                  disableMobileBottomPadding
                                >
                                  <ChatOpen />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          {/* Direct chat: explicit openBy + value (no guessing)
                              openBy: "code" | "id"
                              value: 3-char code OR numeric chat id */}
                          <Route
                            path="/menu/chats/direct/:openBy/:value"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={1}
                                  disableMaxWidth
                                  disableMobileBottomPadding
                                >
                                  <ChatOpen />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/menu/chats/group/:groupChatId"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={1}
                                  disableMaxWidth
                                  disableMobileBottomPadding
                                >
                                  <ChatOpen />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/menu/chats/support/:supportChatId"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={1}
                                  disableMaxWidth
                                  disableMobileBottomPadding
                                >
                                  <ChatOpen />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/search/main"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={0.6}
                                  disableMobileBottomPadding
                                >
                                  <SearchMain />
                                </AppShell>
                              </PrivateRoute >
                            }
                          />
                          <Route
                            path="/search/vault/:code"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={0.6}
                                  disableMobileBottomPadding
                                >
                                  <StorageOpen />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/events/:eventId/party-session"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <PartySessionPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/vault/settings"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={0.9}
                                  disableMobileBottomPadding
                                >
                                  <VaultSettingsPage
                                    authenticatedUser={user}
                                    onAuthenticatedUserChange={setUser}
                                  />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/vault/:storageCode"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={0.9}
                                  disableMobileBottomPadding
                                >
                                  <VaultMain
                                    key={window.location.pathname}
                                    authenticatedUser={user}
                                    onAuthenticatedUserChange={setUser}
                                  />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/vault/main"
                            element={
                              <PrivateRoute user={user} loading={loading}>
                                <AppShell
                                  isGuest={isGuest}
                                  bottomTheme="dark"
                                  sideTheme="dark"
                                  innerFactor={0.9}
                                  disableMobileBottomPadding
                                >
                                  <VaultMain
                                    key="main"
                                    authenticatedUser={user}
                                    onAuthenticatedUserChange={setUser}
                                  />
                                </AppShell>
                              </PrivateRoute>
                            }
                          />
                          {internalToolsAvailable ? (
                            <>
                              <Route
                                path="/network/social-rating"
                                element={
                                  <PrivateRoute user={user} loading={loading}>
                                    {user?.is_staff || user?.is_superuser ? (
                                      <AppShell
                                        isGuest={isGuest}
                                        bottomTheme="dark"
                                        sideTheme="dark"
                                        innerFactor={1.0}
                                        disableMaxWidth
                                        hideBottomNavigation
                                        disableMobileBottomPadding
                                        contentVariant="darkSolid"
                                      >
                                        <SocialRatingDebugPage />
                                      </AppShell>
                                    ) : (
                                      <Navigate to="/vault/main" replace />
                                    )}
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path="/debug/sport-rating"
                                element={
                                  <PrivateRoute user={user} loading={loading}>
                                    {user?.is_staff || user?.is_superuser ? (
                                      <AppShell
                                        isGuest={isGuest}
                                        bottomTheme="dark"
                                        sideTheme="dark"
                                        innerFactor={1.0}
                                        disableMaxWidth
                                        hideBottomNavigation
                                        disableMobileBottomPadding
                                        contentVariant="darkSolid"
                                      >
                                        <SportRatingDebugPage />
                                      </AppShell>
                                    ) : (
                                      <Navigate to="/vault/main" replace />
                                    )}
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path="/network/singles"
                                element={
                                  <PrivateRoute user={user} loading={loading}>
                                    {user?.is_staff || user?.is_superuser ? (
                                      <AppShell
                                        isGuest={isGuest}
                                        bottomTheme="dark"
                                        sideTheme="dark"
                                        innerFactor={1.0}
                                        disableMaxWidth
                                        hideBottomNavigation
                                        disableMobileBottomPadding
                                        contentVariant="darkSolid"
                                      >
                                        <SinglesAnalyticsPage />
                                      </AppShell>
                                    ) : (
                                      <Navigate to="/vault/main" replace />
                                    )}
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path="/network/dinners"
                                element={
                                  <PrivateRoute user={user} loading={loading}>
                                    {user?.is_staff || user?.is_superuser ? (
                                      <AppShell
                                        isGuest={isGuest}
                                        bottomTheme="dark"
                                        sideTheme="dark"
                                        innerFactor={1.0}
                                        disableMaxWidth
                                        hideBottomNavigation
                                        disableMobileBottomPadding
                                        contentVariant="darkSolid"
                                      >
                                        <DinnersAdminPage />
                                      </AppShell>
                                    ) : (
                                      <Navigate to="/vault/main" replace />
                                    )}
                                  </PrivateRoute>
                                }
                              />
                              <Route path="/debug/social-rating" element={<Navigate to="/network/social-rating" replace />} />
                              <Route path="/debug/singles-analytics" element={<Navigate to="/network/singles" replace />} />
                              <Route path="/debug/dinners" element={<Navigate to="/network/dinners" replace />} />
                              {/* Networking Roulette admin (staff only, web-only) */}
                              <Route
                                path="/roulette"
                                element={
                                  <PrivateRoute user={user} loading={loading}>
                                    <RouletteAdminPage />
                                  </PrivateRoute>
                                }
                              />
                            </>
                          ) : null}
                          <Route
                            path="/partner-place-map-picker/*"
                            element={<PartnerPlaceMapPickerPage />}
                          />
                          {/* fallback */}
                          <Route
                            path="*"
                            element={
                              <AppShell
                                isGuest={isGuest}
                                bottomTheme="dark"
                                sideTheme="dark"
                                innerFactor={1.0}
                                disableMaxWidth
                              >
                                <Error404 />
                              </AppShell>
                            }
                          />
                              </Routes>
                              {guideOverlayOpen ? <VaultGuidePlatformPage user={user} isGuest={isGuest} overlay /> : null}

                              {!isPublicationCitySandbox ? <CityLaunchContext.Consumer>
                                {({ hasPriority: cityLaunchHasPriority }) => (
                              <RequiredActionsContext.Consumer>
                                {({ loading: requiredActionsLoading, open: requiredOpen }) => (
                                  <EvaluationContext.Consumer>
                                    {({ hasBlocking, open: evaluationOpen }) => (
                                      <>
                                        {/* Required actions modal – highest priority */}
                                        <RequiredActionsModal />
                                        <ReportedAgePromptModal
                                          userId={!loading ? user?.id : null}
                                          blocked={requiredActionsLoading || requiredOpen || cityLaunchHasPriority || mobileAppInviteHasPriority || (hasBlocking && evaluationOpen)}
                                          onPriorityChange={setAgePromptHasPriority}
                                        />

                                        <MobileAppInviteController
                                          userId={user?.id}
                                          blocked={requiredActionsLoading || requiredOpen || cityLaunchHasPriority}
                                          onPriorityChange={setMobileAppInviteHasPriority}
                                        />

                                        {/* Mutual connections prompt – only after required actions */}
                                        {!requiredOpen && !mobileAppInviteHasPriority && !modalCampaignHasPriority && !agePromptHasPriority && !cityLaunchHasPriority ? <MutualConnectionsPromptModal /> : null}
                                        {!requiredOpen && !mobileAppInviteHasPriority && !modalCampaignHasPriority && !agePromptHasPriority && !cityLaunchHasPriority ? <HostCapacityUpsellPromptModal /> : null}

                                        {/* Keep the evaluation mounted while a higher-priority prompt is
                                            visible so polling can never reset its current participant/phase. */}
                                        <RatingModal
                                          suppressed={
                                            requiredOpen ||
                                            mobileAppInviteHasPriority || cityLaunchHasPriority || agePromptHasPriority
                                          }
                                        />

                                        {/* Streak modal – only when no blocking rating and no higher-priority prompts */}
                                        {!requiredOpen && !mobileAppInviteHasPriority && !cityLaunchHasPriority && !hasBlocking && !agePromptHasPriority ? (
                                          <CheckinContext.Consumer>
                                            {({ showModal, setShowModal }) => (
                                              <CheckinModal
                                                visible={showModal}
                                                onClose={() => setShowModal(false)}
                                              />
                                            )}
                                          </CheckinContext.Consumer>
                                        ) : null}

                                        <CheckinContext.Consumer>
                                          {({ showModal }) => (
                                            <ModalCampaignController
                                              userId={!loading ? user?.id : null}
                                              language={language}
                                              blocked={requiredActionsLoading || requiredOpen || cityLaunchHasPriority || mobileAppInviteHasPriority || hasBlocking || showModal || agePromptHasPriority}
                                              onPriorityChange={setModalCampaignHasPriority}
                                            />
                                          )}
                                        </CheckinContext.Consumer>
                                      </>
                                    )}
                                  </EvaluationContext.Consumer>
                                )}
                              </RequiredActionsContext.Consumer>
                                )}
                              </CityLaunchContext.Consumer> : null}

                              <GlobalErrorModal />
                              </ShopModeProvider>
                              </CityLaunchProvider>
                            </CheckinProvider>
                          </RequiredActionsProvider>
                        </EvaluationProvider>
                      </KeysContext.Provider>
                    </CastleSkinProvider>
                    <NotificationToast />
                    <ChatMessageNotificationToast />
                    </MutualUnreadProvider>
                    </ParticipantRatingUnreadProvider>
                    </ClubRequestUnreadProvider>
                    </ClubMemberUnreadProvider>
                    </ClubAttentionProvider>
                    </HostFeedbackUnreadProvider>
                    </ParticipantRequestUnreadProvider>
                    </WaitlistUnreadProvider>
                    </EventHostAttentionProvider>
                  </StatisticsUnreadProvider>
                  </ChatRealtimeProvider>
                </ChatUnreadProvider>
              </MailboxProvider>
              </TasksProvider>
            </AppStateProvider>
          ) : (
            // Guest stack: no Tasks/Mailbox/Notification providers to avoid private API calls
            <CastleSkinProvider>
              <KeysContext.Provider value={keysCtxValue}>
                <CheckinProvider>
                  <ShopModeProvider>
                    <Routes location={location}>
                      <Route path="/ig-gate" element={<IGGate />} />
                      <Route
                        path="/legal/terms"
                        element={
                          <AppShell
                            isGuest={isGuest}
                            bottomTheme="dark"
                            sideTheme="dark"
                            disableMaxWidth
                            disableMobileBottomPadding
                            hideBottomNavigation
                          >
                            <UserAgreement />
                          </AppShell>
                        }
                      />
                      <Route
                        path="/legal/privacy"
                        element={
                          <AppShell
                            isGuest={isGuest}
                            bottomTheme="dark"
                            sideTheme="dark"
                            disableMaxWidth
                            disableMobileBottomPadding
                            hideBottomNavigation
                          >
                            <PrivacyPolicy />
                          </AppShell>
                        }
                      />
                      <Route
                        path="/legal/community-rules"
                        element={
                          <AppShell
                            isGuest={isGuest}
                            bottomTheme="dark"
                            sideTheme="dark"
                            disableMaxWidth
                            disableMobileBottomPadding
                            hideBottomNavigation
                          >
                            <CommunityRules />
                          </AppShell>
                        }
                      />
                      <Route
                        path="/legal/moderation"
                        element={
                          <AppShell
                            isGuest={isGuest}
                            bottomTheme="dark"
                            sideTheme="dark"
                            disableMaxWidth
                            disableMobileBottomPadding
                            hideBottomNavigation
                          >
                            <ModerationPolicy />
                          </AppShell>
                        }
                      />
                      <Route
                        path="/legal/cookies"
                        element={
                          <AppShell
                            isGuest={isGuest}
                            bottomTheme="dark"
                            sideTheme="dark"
                            disableMaxWidth
                            disableMobileBottomPadding
                            hideBottomNavigation
                          >
                            <CookiesPolicy />
                          </AppShell>
                        }
                      />
                      <Route
                        path="/legal/child-safety"
                        element={
                          <AppShell
                            isGuest={isGuest}
                            bottomTheme="dark"
                            sideTheme="dark"
                            disableMaxWidth
                            disableMobileBottomPadding
                            hideBottomNavigation
                          >
                            <ChildSafetyStandards />
                          </AppShell>
                        }
                      />
                      <Route
                        path="/account-deletion"
                        element={
                          <AppShell
                            isGuest={isGuest}
                            bottomTheme="dark"
                            sideTheme="dark"
                            disableMaxWidth
                            disableMobileBottomPadding
                            hideBottomNavigation
                          >
                            <AccountDeletionPage />
                          </AppShell>
                        }
                      />
                      <Route
                        path="/silent-disco"
                        element={
                          <AppShell
                            isGuest={isGuest}
                            bottomTheme="dark"
                            sideTheme="dark"
                            innerFactor={0.6}
                          >
                            <SilentDiscoPage />
                          </AppShell>
                        }
                      />
                      <Route
                        path="/clubs"
                        element={
                          <AppShell
                            isGuest={isGuest}
                            bottomTheme="dark"
                            sideTheme="dark"
                            innerFactor={0.6}
                            disableMobileBottomPadding
                          >
                            <ClubsPage isGuest />
                          </AppShell>
                        }
                      />
                      <Route
                        path="/clubs/:id"
                        element={
                          <AppShell
                            isGuest={isGuest}
                            bottomTheme="dark"
                            sideTheme="dark"
                            innerFactor={0.6}
                            disableMobileBottomPadding
                          >
                            <ClubPublicPage isGuest />
                          </AppShell>
                        }
                      />
                      <Route
                        path="/events"
                        element={<Navigate to="/events/search" replace />}
                      />
                      <Route
                        path="/events/search"
                        element={
                          <AppShell
                            isGuest={isGuest}
                            bottomTheme="dark"
                            sideTheme="dark"
                            innerFactor={0.6}
                            disableMobileBottomPadding
                          >
                            <ListSearch isGuest />
                          </AppShell>
                        }
                      />
                      <Route
                        path="/events/map_search"
                        element={
                          <AppShell isGuest={isGuest} bottomTheme="dark" sideTheme="dark" innerFactor={1.0} disableMaxWidth disableMobileBottomPadding>
                            <MapSearch isGuest />
                          </AppShell>
                        }
                      />
                      <Route
                        path="/events/hosting-rules"
                        element={<AuthRedirect />}
                      />
                      <Route
                        path="/events/participant-rating"
                        element={<AuthRedirect />}
                      />
                      <Route
                        path="/events/my-tickets"
                        element={<AuthRedirect />}
                      />
                      <Route
                        path="/events/:id/buy/*"
                        element={<EventBuyRedirect />}
                      />
                      <Route
                        path="/events/:id/gallery"
                        element={<AuthRedirect />}
                      />
                      <Route
                        path="/events/:id"
                        element={<EventPublicPage isGuest={isGuest} />}
                      />
                      <Route
                        path="/assistant/core"
                        element={
                          <AppShell
                            isGuest={isGuest}
                            bottomTheme="dark"
                            sideTheme="dark"
                            innerFactor={1.0}
                            disableMaxWidth
                            disableMobileBottomPadding
                          >
                            <AssistantCorePage isGuest={isGuest} />
                          </AppShell>
                        }
                      />
                      <Route
                        path="/host-guide"
                        element={
                          <AppShell
                            isGuest={isGuest}
                            bottomTheme="dark"
                            sideTheme="dark"
                            innerFactor={0.6}
                            disableMobileBottomPadding
                          >
                            <HostGuide isGuest={isGuest} />
                          </AppShell>
                        }
                      />
                      <Route
                        path="/commercial-conditions"
                        element={
                          <AppShell
                            isGuest={isGuest}
                            bottomTheme="dark"
                            sideTheme="dark"
                            innerFactor={0.72}
                            disableMobileBottomPadding
                          >
                            <CommercialConditionsPage isGuest={isGuest} />
                          </AppShell>
                        }
                      />
                      <Route
                        path="/commercial-conditions/en"
                        element={
                          <AppShell
                            isGuest={isGuest}
                            bottomTheme="dark"
                            sideTheme="dark"
                            innerFactor={0.72}
                            disableMobileBottomPadding
                          >
                            <CommercialConditionsPage forcedLanguageCode="en" isGuest={isGuest} />
                          </AppShell>
                        }
                      />
                      <Route
                        path="/commercial-conditions/ru"
                        element={
                          <AppShell
                            isGuest={isGuest}
                            bottomTheme="dark"
                            sideTheme="dark"
                            innerFactor={0.72}
                            disableMobileBottomPadding
                          >
                            <CommercialConditionsPage forcedLanguageCode="ru" isGuest={isGuest} />
                          </AppShell>
                        }
                      />
                      <Route
                        path="/commercial-conditions/sk"
                        element={
                          <AppShell
                            isGuest={isGuest}
                            bottomTheme="dark"
                            sideTheme="dark"
                            innerFactor={0.72}
                            disableMobileBottomPadding
                          >
                            <CommercialConditionsPage forcedLanguageCode="sk" isGuest={isGuest} />
                          </AppShell>
                        }
                      />
                      <Route
                        path="/paid-host-verification-terms"
                        element={<PaidHostLegalPage variant="verification" />}
                      />
                      <Route
                        path="/paid-host-verification-terms/en"
                        element={<PaidHostLegalPage variant="verification" forcedLanguageCode="en" />}
                      />
                      <Route
                        path="/paid-host-verification-terms/ru"
                        element={<PaidHostLegalPage variant="verification" forcedLanguageCode="ru" />}
                      />
                      <Route
                        path="/paid-host-verification-terms/sk"
                        element={<PaidHostLegalPage variant="verification" forcedLanguageCode="sk" />}
                      />
                      <Route
                        path="/paid-host-agreement"
                        element={<PaidHostLegalPage variant="agreement" />}
                      />
                      <Route
                        path="/paid-host-agreement/en"
                        element={<PaidHostLegalPage variant="agreement" forcedLanguageCode="en" />}
                      />
                      <Route
                        path="/paid-host-agreement/ru"
                        element={<PaidHostLegalPage variant="agreement" forcedLanguageCode="ru" />}
                      />
                      <Route
                        path="/paid-host-agreement/sk"
                        element={<PaidHostLegalPage variant="agreement" forcedLanguageCode="sk" />}
                      />
                      <Route
                        path="/paid-ticket-participant-terms"
                        element={<PaidTicketParticipantTermsPage />}
                      />
                      <Route
                        path="/paid-ticket-participant-terms/en"
                        element={<PaidTicketParticipantTermsPage forcedLanguageCode="en" />}
                      />
                      <Route
                        path="/paid-ticket-participant-terms/ru"
                        element={<PaidTicketParticipantTermsPage forcedLanguageCode="ru" />}
                      />
                      <Route
                        path="/paid-ticket-participant-terms/sk"
                        element={<PaidTicketParticipantTermsPage forcedLanguageCode="sk" />}
                      />
                      <Route
                        path="/host-guide/en"
                        element={
                          <AppShell
                            isGuest={isGuest}
                            bottomTheme="dark"
                            sideTheme="dark"
                            innerFactor={0.6}
                            disableMobileBottomPadding
                          >
                            <HostGuide forcedLanguageCode="en" isGuest={isGuest} />
                          </AppShell>
                        }
                      />
                      <Route
                        path="/host-guide/sk"
                        element={
                          <AppShell
                            isGuest={isGuest}
                            bottomTheme="dark"
                            sideTheme="dark"
                            innerFactor={0.6}
                            disableMobileBottomPadding
                          >
                            <HostGuide forcedLanguageCode="sk" isGuest={isGuest} />
                          </AppShell>
                        }
                      />
                      <Route
                        path="/host-guide/cs"
                        element={
                          <AppShell
                            isGuest={isGuest}
                            bottomTheme="dark"
                            sideTheme="dark"
                            innerFactor={0.6}
                            disableMobileBottomPadding
                          >
                            <HostGuide forcedLanguageCode="cs" isGuest={isGuest} />
                          </AppShell>
                        }
                      />
                      <Route path="/hostguide" element={<Navigate to="/host-guide" replace />} />
                      <Route path="/hostguide/en" element={<Navigate to="/host-guide/en" replace />} />
                      <Route path="/hostguide/sk" element={<Navigate to="/host-guide/sk" replace />} />
                          <Route path="/hostguide/cs" element={<Navigate to="/host-guide/cs" replace />} />
                      <Route path="/host-guide/" element={<Navigate to="/host-guide" replace />} />
                      <Route path="/host-guide/en/" element={<Navigate to="/host-guide/en" replace />} />
                      <Route path="/host-guide/sk/" element={<Navigate to="/host-guide/sk" replace />} />
                          <Route path="/host-guide/cs/" element={<Navigate to="/host-guide/cs" replace />} />
                      <Route path="/commercial-conditions/" element={<Navigate to="/commercial-conditions" replace />} />
                      <Route path="/commercial-conditions/en/" element={<Navigate to="/commercial-conditions/en" replace />} />
                      <Route path="/commercial-conditions/ru/" element={<Navigate to="/commercial-conditions/ru" replace />} />
                      <Route path="/commercial-conditions/sk/" element={<Navigate to="/commercial-conditions/sk" replace />} />
                      <Route path="/paid-host-verification-terms/" element={<Navigate to="/paid-host-verification-terms" replace />} />
                      <Route path="/paid-host-verification-terms/en/" element={<Navigate to="/paid-host-verification-terms/en" replace />} />
                      <Route path="/paid-host-verification-terms/ru/" element={<Navigate to="/paid-host-verification-terms/ru" replace />} />
                      <Route path="/paid-host-verification-terms/sk/" element={<Navigate to="/paid-host-verification-terms/sk" replace />} />
                      <Route path="/paid-host-agreement/" element={<Navigate to="/paid-host-agreement" replace />} />
                      <Route path="/paid-host-agreement/en/" element={<Navigate to="/paid-host-agreement/en" replace />} />
                      <Route path="/paid-host-agreement/ru/" element={<Navigate to="/paid-host-agreement/ru" replace />} />
                      <Route path="/paid-host-agreement/sk/" element={<Navigate to="/paid-host-agreement/sk" replace />} />
                      <Route path="/paid-ticket-participant-terms/" element={<Navigate to="/paid-ticket-participant-terms" replace />} />
                      <Route path="/paid-ticket-participant-terms/en/" element={<Navigate to="/paid-ticket-participant-terms/en" replace />} />
                      <Route path="/paid-ticket-participant-terms/ru/" element={<Navigate to="/paid-ticket-participant-terms/ru" replace />} />
                      <Route path="/paid-ticket-participant-terms/sk/" element={<Navigate to="/paid-ticket-participant-terms/sk" replace />} />
                      {/* Web-only modal sandbox; staff guard lives inside the page. */}
                      {internalToolsAvailable ? (
                        <Route
                          path="/local-debug/modal-sandbox"
                          element={
                            localDebugRoutesEnabled ? (
                              <AppShell
                                isGuest
                                bottomTheme="dark"
                                sideTheme="dark"
                                innerFactor={1.0}
                                disableMaxWidth
                                disableMobileBottomPadding
                              >
                                <ModalSandboxPage user={null} />
                              </AppShell>
                            ) : (
                              <AuthRedirect />
                            )
                          }
                        />
                      ) : null}
                      {localDebugRoutesEnabled ? (
                        <Route
                          path="/local-debug/paid-event-creation"
                          element={
                            <AppShell
                              isGuest
                              bottomTheme="dark"
                              sideTheme="dark"
                              innerFactor={1.0}
                              disableMaxWidth
                              disableMobileBottomPadding
                              hideBottomNavigation
                            >
                              <EventCreateForm sandboxMode />
                            </AppShell>
                          }
                        />
                      ) : null}
                      {localDebugRoutesEnabled ? (
                        <Route
                          path="/local-debug/publication-city-sandbox"
                          element={<AppShell isGuest bottomTheme="dark" sideTheme="dark" innerFactor={1.0} disableMaxWidth disableMobileBottomPadding hideBottomNavigation><PublicationCitySandboxPage /></AppShell>}
                        />
                      ) : null}
                      {localDebugRoutesEnabled ? (
                        <Route
                          path="/local-debug/onboarding-sandbox"
                          element={<AuthRedirect />}
                        />
                      ) : null}
                      {localDebugRoutesEnabled ? (
                        <Route
                          path="/local-debug/registration-onboarding"
                          element={
                            <AppShell
                              isGuest
                              bottomTheme="dark"
                              sideTheme="dark"
                              innerFactor={1.0}
                              disableMaxWidth
                              disableMobileBottomPadding
                              hideBottomNavigation
                            >
                              <AuthPage
                                registrationOnboardingFlowPreview
                                onAuth={() => {}}
                              />
                            </AppShell>
                          }
                        />
                      ) : null}
                      {localDebugRoutesEnabled ? (
                        <Route
                          path="/local-debug/registration-onboarding/cards"
                          element={<RegistrationOnboardingPreviewPage user={user} />}
                        />
                      ) : null}
                      {localDebugRoutesEnabled ? (
                        <Route path="/local-debug/vault-guide" element={<VaultGuidePlatformPage user={user} isGuest />} />
                      ) : null}
                      <Route path="/guide" element={<AuthRedirect />} />
                      {localDebugRoutesEnabled ? (
                        <Route path="/local-debug/subscription-payment" element={<SubscriptionPaymentPreviewPage />} />
                      ) : null}
                      <Route path="/guide" element={<AuthRedirect />} />
                      {localDebugRoutesEnabled ? (
                        <Route path="/local-debug/key-selector" element={<KeySelectorPreviewPage />} />
                      ) : null}
                      <Route path="/registration/onboarding" element={<AuthRedirect />} />
                      {localDebugRoutesEnabled ? (
                        <Route
                          path="/local-debug/invoice-preview"
                          element={
                            <AppShell
                              isGuest={isGuest}
                              bottomTheme="dark"
                              sideTheme="dark"
                              innerFactor={1.0}
                              disableMaxWidth
                              disableMobileBottomPadding
                            >
                              <InvoicePreviewPage />
                            </AppShell>
                          }
                        />
                      ) : null}
                      <Route path="/shop/*" element={<AuthRedirect />} />
                      <Route path="/partner-place-map-picker/*" element={<PartnerPlaceMapPickerPage />} />
                      <Route path="/menu/*" element={<AuthRedirect />} />
                      <Route path="/vault/*" element={<AuthRedirect />} />
                      <Route path="/search/*" element={<AuthRedirect />} />
                      {/* Any other path → auth */}
                      <Route path="*" element={<AuthRedirect />} />
                    </Routes>
                    <GlobalErrorModal />
                  </ShopModeProvider>
                </CheckinProvider>
              </KeysContext.Provider>
            </CastleSkinProvider>
          )
        )}
        <HostClaimModal
          visible={hostClaimOpen}
          token={hostClaimToken}
          onClose={handleCloseHostClaim}
          onSuccess={handleCloseHostClaim}
        />
        <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[10000] hidden justify-center px-4 pb-4 pt-2 lg:flex">
          <CookieConsentBanner isDesktop />
        </div>
        <div
          className="pointer-events-none fixed inset-x-0 bottom-0 z-[10000] flex justify-center px-3 pb-[calc(env(safe-area-inset-bottom,0px)+4px)] pt-2 lg:hidden"
        >
          <CookieConsentBanner />
        </div>
        <AssistantNotificationToast />
        <EventPromoUploadStatus userId={user?.id} />
        <GuestAssistantLauncher isGuest={isGuest} />
      </RouterGuard>
      </ProfileCardProvider>
      </AssistantUnreadProvider>
      </React.Suspense>
    </QueryClientProvider>
  );
}

export default App;
